import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import FeasibilityScore from './FeasibilityScore';
import { buildSearchResultsForFeasibility } from '../utils/buildSearchResultsForFeasibility';
import {
  buildSourceHealthFromApiErrors,
  getAiDisabledReasonFromSourceHealth,
  getLimitedDataWarningText,
} from '../utils/sourceHealth';
import LocationMap from './LocationMap';
import MarketData from './MarketData';
import GovMapData from './GovMapData';
import SectionNav from './SectionNav';
import PlanDetailsDrawer from './PlanDetailsDrawer';
import AnalyticsService from '../services/AnalyticsService';
import apiConfig from '../config/api';
import './section-header-styles.css';
import './MarketData.css';
import { 
  ResultsPageShimmer, 
  TelAvivShimmer,
  MunicipalSectionShimmer,
  MarketDataShimmer
} from './ShimmerComponents';

const normalizeForDedup = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/["'׳״`]/g, '')
    .toLowerCase();

const normalizeObjectivesText = (value) =>
  String(value || '')
    .replace(/\s*\^\s*/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const isPlaceholderObjectives = (text) => {
  const normalized = String(text || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return true;
  return (
    normalized === '-' ||
    normalized === '—' ||
    normalized.toLowerCase() === 'n/a' ||
    normalized === 'לא ידוע' ||
    normalized === 'לא זמין' ||
    normalized === 'אין נתונים' ||
    normalized === 'אין מידע'
  );
};

const isLikelyLowQualityObjectives = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return true;
  const compact = raw.replace(/\s+/g, '');
  if (!compact) return true;
  const total = compact.length;
  const hebrew = (compact.match(/[\u0590-\u05FF]/g) || []).length;
  const latin = (compact.match(/[A-Za-z]/g) || []).length;
  const replacement = (compact.match(/�/g) || []).length;
  const symbols = (compact.match(/[^\u0590-\u05FFA-Za-z0-9.,:;!?'"()\-/%]/g) || []).length;
  const bidiMarks = (raw.match(/[\u200E\u200F\u202A-\u202E]/g) || []).length;
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const shortLines = lines.filter((line) => line.length <= 3).length;
  const hebrewRatio = hebrew / total;
  const latinRatio = latin / total;
  const noiseRatio = (replacement + symbols) / total;
  const shortLineRatio = lines.length ? shortLines / lines.length : 0;
  return hebrewRatio < 0.25 || latinRatio > 0.2 || noiseRatio > 0.08 || bidiMarks > 5 || (shortLineRatio > 0.28 && lines.length > 10);
};

const getPlanObjectives = (plan) => {
  const candidates = [
    plan?.mavatObjectivesFull,
    plan?.mavat_objectives_full,
    plan?.iplanObjectives,
    plan?.iplan_objectives,
    plan?.objectives,
    plan?.pl_objectives,
    plan?.plan_objectives,
  ];
  for (const candidate of candidates) {
    const cleaned = normalizeObjectivesText(candidate);
    if (!cleaned) continue;
    if (isPlaceholderObjectives(cleaned)) continue;
    if (isLikelyLowQualityObjectives(cleaned)) continue;
    return cleaned;
  }
  return null;
};

const getPlanObjectivesAny = (plan) => {
  const candidates = [
    plan?.mavatObjectivesFull,
    plan?.mavat_objectives_full,
    plan?.iplanObjectives,
    plan?.iplan_objectives,
    plan?.objectives,
    plan?.pl_objectives,
    plan?.plan_objectives,
  ];
  for (const candidate of candidates) {
    const cleaned = normalizeObjectivesText(candidate);
    if (isPlaceholderObjectives(cleaned)) continue;
    if (cleaned) return cleaned;
  }
  return null;
};

const normalizePlanLookupId = (value) =>
  String(value || '')
    .trim()
    .replace(/["'׳״`]/g, '')
    .replace(/[^0-9A-Za-z\u0590-\u05FF]/g, '')
    .toLowerCase();

const buildPlanDedupKey = (plan) => {
  if (plan.requestNumber || plan.permitNumber || plan.isTelAvivPermit || plan.isComplotPermit || plan.isMunicipalPermit) {
    const permitNumber = normalizeForDedup(plan.requestNumber || plan.permitNumber || plan.id);
    const scope = normalizeForDedup(plan.municipality || plan.county || plan.municipalSource || plan.source || '');
    return `permit|${permitNumber}|${scope}`;
  }

  const planNumber = normalizeForDedup(plan.planNumber || plan.plan_number || '');
  const title = normalizeForDedup(plan.title || '');
  const scope = normalizeForDedup(plan.municipality || plan.county || plan.municipalSource || plan.source || '');

  if (planNumber) return `plan-number|${planNumber}|${scope}`;

  if (plan.sourceLayer && plan.sourceObjectId) {
    return `plan-layer|${plan.sourceLayer}|${plan.sourceObjectId}`;
  }
  return `plan-title|${title}|${scope}`;
};

const scorePlanForDedup = (plan) => {
  let score = 0;
  if (plan.sourceLayer) score += 2;
  if (plan.sourceObjectId) score += 2;
  if (getPlanObjectives(plan)) score += 2;
  if (plan.planUrl || plan.plan_url) score += 1;
  if (plan.approvalDate) score += 1;
  if (plan.submissionDate) score += 1;
  if (plan.location) score += 1;
  if (plan.approvingAuthority) score += 1;
  return score;
};

const ResultsPage = ({ results, address, propertyInfo, isLoading = false, onBack, embedded = false, apiErrors = {}, onRetryPropertyInfo, onRetryPlans, feasibilityData = null, onApplyFeasibilityOverride, feasibilityOverrideBusy = false, isParcelSearch = false, activeTab = 'property', onTabChange, aiRecommendation, setAiRecommendation, aiAnalysisUnlocked, setAiAnalysisUnlocked, aiLoading, setAiLoading, aiError, setAiError }) => {
  const [otherResultsExpanded, setOtherResultsExpanded] = useState(false);
  const [telAvivExpanded, setTelAvivExpanded] = useState(true); // Start expanded
  const [telAvivKlalExpanded, setTelAvivKlalExpanded] = useState(false);
  const [telAvivLocalExpanded, setTelAvivLocalExpanded] = useState(false);
  const [rehovotExpanded, setRehovotExpanded] = useState(false); // Collapsed by default
  const [jerusalemExpanded, setJerusalemExpanded] = useState(false); // Collapsed by default
  // Removed ramatGanExpanded - now using dynamic complotExpanded for all Complot municipalities
  const [complotExpanded, setComplotExpanded] = useState({}); // Dynamic state for Complot municipalities
  // eslint-disable-next-line no-unused-vars
  const [resultsMinimized, setResultsMinimized] = useState(false); // Control if results are minimized
  const [searchTimestamp] = useState(() => new Date());
  const resultsTopRef = useRef(null);
  const resultsViewedKeyRef = useRef('');
  const alertsSignupStartedRef = useRef(false);

  const feasibilitySearchResults = useMemo(
    () => buildSearchResultsForFeasibility({ address, propertyInfo, results }),
    [address, propertyInfo, results]
  );

  const sourceHealth = useMemo(() => {
    const healthFromErrors = buildSourceHealthFromApiErrors(apiErrors);
    if (healthFromErrors.criticalSourceUnavailable) return healthFromErrors;
    return results?.sourceHealth || healthFromErrors;
  }, [apiErrors, results]);

  const aiDisabledReason = getAiDisabledReasonFromSourceHealth(sourceHealth);
  
  // Table sorting and filtering state
  const [tableSortConfig, setTableSortConfig] = useState({}); // { sectionType: { column: 'name', direction: 'asc' } }
  const [tableFilters, setTableFilters] = useState({}); // { sectionType: 'filterText' }
  const [expandedObjectives, setExpandedObjectives] = useState({}); // { 'sectionType-planId': true/false }
  const [selectedPlanForDrawer, setSelectedPlanForDrawer] = useState(null);
  const [hydratedObjectivesByPlanNumber, setHydratedObjectivesByPlanNumber] = useState({});
  const hydratedObjectivesRef = useRef({});
  const objectivesHydrationInFlightRef = useRef(new Set());
  const objectivesHydrationPendingRef = useRef(new Map());
  const objectivesHydrationTimerRef = useRef(null);
  const rawBuildingData = propertyInfo?.buildingData || results?.buildingData || null;

  // Cross-source floor/height sanity check: if floors and height are available
  // (from any source) and they don't make sense together, correct floors and flag it.
  const effectiveBuildingData = (() => {
    if (!rawBuildingData) return null;
    if (rawBuildingData.floorsOverridden) return rawBuildingData;

    const rawFloors = rawBuildingData.floors ? parseInt(rawBuildingData.floors) : NaN;
    const bestHeight = parseFloat(rawBuildingData.height)
      || parseFloat(propertyInfo?.advancedLayers?.buildingStats?.['גובה המבנה במטרים'])
      || NaN;

    if (!isNaN(rawFloors) && rawFloors > 0 && !isNaN(bestHeight) && bestHeight > 0) {
      const impliedFloorHeight = bestHeight / rawFloors;
      if (impliedFloorHeight > 6) {
        const corrected = Math.floor(bestHeight / 3.6);
        if (corrected >= 1 && corrected !== rawFloors) {
          return {
            ...rawBuildingData,
            floors: rawFloors,
            correctedFloors: corrected,
            floorsOverridden: true,
            floorsOverrideReason: `נתון זה אינו תואם לגובה המבנה (${bestHeight.toFixed ? bestHeight.toFixed(1) : bestHeight} מ׳). המערכת משתמשת ב-${corrected} קומות (הערכה) לצורך החישובים.`
          };
        }
      }
    }
    return rawBuildingData;
  })();

  // Debug: Log feasibility data when it changes
  useEffect(() => {
    console.log('🔍 MarketData debug:', { 
      hasFeasibilityData: !!feasibilityData,
      hasFeasibilityInput: !!feasibilityData?.feasibilityInput,
      marketData: feasibilityData?.feasibilityInput?.marketData,
      expectedSalePricePerSqmNIS: feasibilityData?.feasibilityInput?.expectedSalePricePerSqmNIS,
      fullFeasibilityInput: feasibilityData?.feasibilityInput
    });
  }, [feasibilityData]);

  // Updates form state
  const [updatesEmail, setUpdatesEmail] = useState('');
  const [updatesErrors, setUpdatesErrors] = useState({});
  const [updatesSuccess, setUpdatesSuccess] = useState(false);
  const [isSubmittingUpdates, setIsSubmittingUpdates] = useState(false); // Add loading state
  const isParcelOnlyLabel = (value) => /^גוש\s+\S+[,/ ]+\s*חלקה\s+\S+/i.test(String(value || '').trim());
  const displayPropertyAddress = [
    propertyInfo?.fullAddress,
    propertyInfo?.searchAddress,
    results?.address,
    !isParcelOnlyLabel(address) ? address : '',
  ].find((value) => value && !isParcelOnlyLabel(value));
  const alertPropertyLabel = displayPropertyAddress || [
    propertyInfo?.gush ? `גוש ${propertyInfo.gush}` : '',
    propertyInfo?.helka ? `חלקה ${propertyInfo.helka}` : '',
    propertyInfo?.municipality || propertyInfo?.municipalityName || '',
  ].filter(Boolean).join(', ') || 'הנכס שנבדק';

  const handleUpdatesSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!updatesEmail.trim()) {
      newErrors.email = 'נא למלא אימייל';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatesEmail)) {
      newErrors.email = 'נא להזין כתובת אימייל תקינה';
    }

    if (Object.keys(newErrors).length > 0) {
      setUpdatesErrors(newErrors);
      AnalyticsService.trackAlertsSignupFailed({
        municipality: propertyInfo?.municipality || propertyInfo?.municipalityName || results?.municipality,
        searchMode: isParcelSearch ? 'parcel' : 'address',
        sourceSurface: 'results_page',
        errorCode: newErrors.email ? 'invalid_email' : 'validation_failed',
      });
      return;
    }

    setIsSubmittingUpdates(true); // Start loading

    const coords = propertyInfo?.coordinates || results?.coordinates;
    const itmCentroid = results?.itmCoords || results?.parcelGeometry?.centroid;
    const payload = {
      name: alertPropertyLabel, 
      email: updatesEmail, 
      address: alertPropertyLabel,
      gush: propertyInfo?.gush || '',
      helka: propertyInfo?.helka || '',
      municipality: propertyInfo?.municipality || propertyInfo?.municipalityName || '',
      coordinateX: coords?.x || '',
      coordinateY: coords?.y || '',
      itmX: itmCentroid?.x || '',
      itmY: itmCentroid?.y || '',
      sourceSurface: 'results_page',
      attribution: AnalyticsService.getAttributionParams(),
    };

    try {
      const response = await fetch(`${apiConfig.apiBaseUrl}/api/alerts/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'לא הצלחנו לבצע הרשמה להתראות כרגע');
      }

      AnalyticsService.trackAlertsSignupSubmitted({
        municipality: payload.municipality,
        searchMode: isParcelSearch ? 'parcel' : 'address',
        sourceSurface: 'results_page',
        hasResults: Array.isArray(results?.plans) ? results.plans.length > 0 : Array.isArray(results) ? results.length > 0 : false,
        resultsCount: Array.isArray(results?.plans) ? results.plans.length : Array.isArray(results) ? results.length : 0,
      });
      setUpdatesErrors({});
      setUpdatesSuccess(true);
    } catch (error) {
      AnalyticsService.trackAlertsSignupFailed({
        municipality: payload.municipality,
        searchMode: isParcelSearch ? 'parcel' : 'address',
        sourceSurface: 'results_page',
        errorCode: 'submit_failed',
        errorMessage: error.message,
      });
      setUpdatesErrors((prev) => ({
        ...prev,
        submit: error.message || 'לא הצלחנו לבצע הרשמה להתראות כרגע. נסו שוב.',
      }));
    } finally {
      setIsSubmittingUpdates(false);
    }
  };
  
  // Calculate number of sources checked (all APIs called, not just those with results)
  const getSourcesCount = () => {
    let count = 0;
    
    // GovMap for property info
    count++;
    
    // Government IPlan database
    count++;
    
    // Municipal systems (Tel Aviv, Tel Aviv Outline, Rehovot, Jerusalem)
    count += 4;
    
    // Priority areas database
    count++;
    
    // Building archive (if property in Tel Aviv)
    if (propertyInfo?.municipality === 'תל אביב-יפו') {
      count++;
    }
    
    // Betterment levy database
    count++;
    
    return count;
  };

  // Format search timestamp
  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };
  
  // No auto-scroll on results or tab switch (manual user control)

  // Extract municipality from address if not in propertyInfo
  const getMunicipality = () => {
    if (propertyInfo?.municipality) {
      return propertyInfo.municipality;
    }
    // Extract from address
    if (address) {
      if (address.includes('תל אביב') || address.includes('תל-אביב')) return 'תל אביב-יפו';
      if (address.includes('ירושלים')) return 'ירושלים';
      if (address.includes('רחובות')) return 'רחובות';
      if (address.includes('חיפה')) return 'חיפה';
      if (address.includes('באר שבע') || address.includes('באר-שבע')) return 'באר שבע';
      if (address.includes('רמת גן') || address.includes('רמת-גן')) return 'רמת גן';
      if (address.includes('פתח תקווה') || address.includes('פתח-תקווה')) return 'פתח תקווה';
      if (address.includes('אשדוד')) return 'אשדוד';
      if (address.includes('נתניה')) return 'נתניה';
      if (address.includes('בני ברק') || address.includes('בני-ברק')) return 'בני ברק';
    }
    return null;
  };

  const municipality = getMunicipality();

  const formatDisplayDateValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    if (typeof value === 'number') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('he-IL');
    }

    const s = String(value).trim();
    if (!s) return '-';

    // Keep already formatted DMY dates as-is (normalize dots to slashes).
    if (/^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(s)) {
      return s.replace(/\./g, '/');
    }

    const parsed = Date.parse(s);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toLocaleDateString('he-IL');
    }

    return s;
  };

  const normalizedPlans = useMemo(() => {
    const list = Array.isArray(results?.plans) ? results.plans : (Array.isArray(results) ? results : []);
    const buckets = new Map();

    list.forEach((plan) => {
      // Product requirement: never show nearby-only rows in this screen.
      if (plan?.isNearbyToAddress) return;
      const normalizedPlan = {
        ...plan,
        objectives: getPlanObjectivesAny(plan),
      };
      const key = buildPlanDedupKey(normalizedPlan);
      const existing = buckets.get(key);
      if (!existing || scorePlanForDedup(normalizedPlan) > scorePlanForDedup(existing)) {
        buckets.set(key, normalizedPlan);
      }
    });

    return Array.from(buckets.values());
  }, [results]);

  useEffect(() => {
    if (isLoading || !results) return;
    const key = [
      propertyInfo?.gush || '',
      propertyInfo?.helka || '',
      address || '',
      normalizedPlans.length,
    ].join('|');
    if (resultsViewedKeyRef.current === key) return;
    resultsViewedKeyRef.current = key;
    alertsSignupStartedRef.current = false;
    AnalyticsService.trackResultsViewed({
      address,
      searchMode: isParcelSearch ? 'parcel' : 'address',
      gush: propertyInfo?.gush,
      helka: propertyInfo?.helka,
      municipality: propertyInfo?.municipality || propertyInfo?.municipalityName || results?.municipality,
      hasResults: normalizedPlans.length > 0,
      resultsCount: normalizedPlans.length,
      sourceSurface: 'results_page',
    });
  }, [address, isLoading, isParcelSearch, normalizedPlans.length, propertyInfo, results]);

  const getPlanObjectivesWithHydration = (plan) => {
    const normalizedPlanNumber = normalizePlanLookupId(plan?.planNumber || plan?.plan_number);
    const hydrated = normalizedPlanNumber ? hydratedObjectivesByPlanNumber[normalizedPlanNumber] : null;
    if (hydrated) return hydrated;
    return getPlanObjectivesAny(plan);
  };

  useEffect(() => {
    hydratedObjectivesRef.current = hydratedObjectivesByPlanNumber;
  }, [hydratedObjectivesByPlanNumber]);

  const flushObjectivesHydrationQueue = useCallback(async () => {
    const pendingEntries = Array.from(objectivesHydrationPendingRef.current.entries());
    if (!pendingEntries.length) return;

    const toRequest = pendingEntries.filter(([normalized]) => {
      if (hydratedObjectivesRef.current[normalized]) return false;
      if (objectivesHydrationInFlightRef.current.has(normalized)) return false;
      return true;
    });

    if (!toRequest.length) return;

    const inflightNormalized = toRequest.map(([normalized]) => normalized);
    inflightNormalized.forEach((id) => objectivesHydrationInFlightRef.current.add(id));
    inflightNormalized.forEach((id) => objectivesHydrationPendingRef.current.delete(id));

    const ids = toRequest.map(([, rawId]) => rawId);
    try {
      const response = await fetch(`${apiConfig.apiBaseUrl}/api/ta-objectives-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!response.ok) return;
      const data = await response.json();
      const byPlanNumber = data?.byPlanNumber && typeof data.byPlanNumber === 'object' ? data.byPlanNumber : {};
      if (!Object.keys(byPlanNumber).length) return;
      setHydratedObjectivesByPlanNumber((prev) => ({ ...prev, ...byPlanNumber }));
    } catch {
      // Ignore hydration errors and keep fallback '-'.
    } finally {
      inflightNormalized.forEach((id) => objectivesHydrationInFlightRef.current.delete(id));
    }
  }, []);

  useEffect(() => {
    if (isLoading) return undefined;

    const missingByNormalized = new Map();
    normalizedPlans.forEach((plan) => {
      if (getPlanObjectivesAny(plan)) return;
      const isPermit = Boolean(
        plan?.isTelAvivPermit ||
          plan?.isComplotPermit ||
          plan?.isMunicipalPermit ||
          plan?.requestNumber ||
          plan?.permitNumber
      );
      if (isPermit) return;
      const rawPlanNumber = String(plan?.planNumber || plan?.plan_number || '').trim();
      if (!rawPlanNumber || rawPlanNumber === '-') return;
      const normalized = normalizePlanLookupId(rawPlanNumber);
      if (!normalized) return;
      if (hydratedObjectivesRef.current[normalized]) return;
      if (objectivesHydrationInFlightRef.current.has(normalized)) return;
      if (!missingByNormalized.has(normalized)) {
        missingByNormalized.set(normalized, rawPlanNumber);
      }
    });

    if (!missingByNormalized.size) return undefined;

    for (const [normalized, rawId] of missingByNormalized.entries()) {
      objectivesHydrationPendingRef.current.set(normalized, rawId);
    }

    if (!objectivesHydrationTimerRef.current) {
      objectivesHydrationTimerRef.current = setTimeout(() => {
        objectivesHydrationTimerRef.current = null;
        flushObjectivesHydrationQueue();
      }, 140);
    }

    return () => {
      if (objectivesHydrationTimerRef.current) {
        clearTimeout(objectivesHydrationTimerRef.current);
        objectivesHydrationTimerRef.current = null;
      }
    };
  }, [normalizedPlans, flushObjectivesHydrationQueue, isLoading]);

  const handleViewPlan = (result) => {
    const isPermit = Boolean(
      result.isTelAvivPermit ||
      result.isComplotPermit ||
      result.isMunicipalPermit ||
      result.requestNumber ||
      result.permitNumber
    );
    if (!isPermit) {
      AnalyticsService.trackPlanDetailOpen({
        planNumber: result.planNumber || result.plan_number || result.id,
        title: result.title,
        municipality: result.municipality || result.county,
        sourceSurface: 'results_table',
      });
      setSelectedPlanForDrawer({
        ...result,
        fallbackCoordinates:
          result?.coordinates ||
          propertyInfo?.coordinates ||
          results?.coordinates ||
          null,
      });
      return;
    }

    const primaryNumber = result.requestNumber || result.permitNumber || result.id || 'unknown';
    AnalyticsService.trackPermitDetailOpen({
      requestNumber: result.requestNumber,
      permitNumber: result.permitNumber || primaryNumber,
      title: result.title,
      municipality: result.municipality || result.county,
      sourceSurface: 'results_table',
    });
    setSelectedPlanForDrawer({
      ...result,
      fallbackCoordinates:
        result?.coordinates ||
        propertyInfo?.coordinates ||
        results?.coordinates ||
        null,
    });
  };

  // Helper function to extract and parse dates for sorting
  const getDateForSorting = (plan) => {
    // Debug: Log all fields of first few plans to see what's available
    if (plan.title && plan.title.includes('תוספת שימושי')) {
      console.log('🔍 Available fields in plan:', Object.keys(plan));
      console.log('🔍 Plan object:', plan);
    }
    
    // Try different date fields in order of preference
    const dateFields = [
      plan.submissionDate,
      plan.approvalDate,
      plan.planDate,
      plan.lastModified,
      plan.startDate,
      plan.endDate,
      // Try some other common date field names
      plan.date,
      plan.publicationDate,
      plan.statusDate,
      plan.validFrom,
      plan.validTo,
      plan.depositDate
    ];
    
    for (const dateField of dateFields) {
      if (!dateField) continue;
      
      // Debug: Log the actual date value we're trying to parse
      if (plan.title && plan.title.includes('תוספת שימושי')) {
        console.log('🔍 Trying to parse date field:', dateField, 'type:', typeof dateField);
      }
      
      // Skip non-parseable values
      if (dateField === 'לא ידוע' || dateField === 'תאריך לא תקין') {
        continue;
      }
      
      // Handle different date formats
      let parsedDate = null;
      
      // Hebrew locale date format (e.g., "23.06.1949" or "23/06/1949")
      if (dateField.match(/^\d{1,2}[./]\d{1,2}[./]\d{4}$/)) {
        const parts = dateField.split(/[./]/);
        const [day, month, year] = parts;
        parsedDate = new Date(year, month - 1, day);
      }
      // DD/MM/YYYY format (e.g., "23/06/1949")
      else if (dateField.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateField.split('/');
        parsedDate = new Date(year, month - 1, day);
      }
      // YYYY-MM-DD format
      else if (dateField.match(/^\d{4}-\d{2}-\d{2}/)) {
        parsedDate = new Date(dateField);
      }
      // MM/DD/YYYY format
      else if (dateField.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        parsedDate = new Date(dateField);
      }
      // Just year (YYYY)
      else if (dateField.match(/^\d{4}$/)) {
        parsedDate = new Date(dateField, 0, 1);
      }
      // Try direct parsing for timestamps or other formats
      else if (typeof dateField === 'number' || !isNaN(Date.parse(dateField))) {
        parsedDate = new Date(dateField);
      }
      
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        if (plan.title && plan.title.includes('תוספת שימושי')) {
          console.log('🔍 Successfully parsed date:', dateField, 'to:', parsedDate.toISOString().substring(0, 10));
        }
        return parsedDate;
      }
    }
    
    // Return very old date for plans without valid dates (puts them last)
    return new Date(1900, 0, 1);
  };
  
  // Sort plans by date (newest first)
  const sortPlansByDate = (plans) => {
    let comparisonCount = 0;
    
    const sorted = [...plans].sort((a, b) => {
      const dateA = getDateForSorting(a);
      const dateB = getDateForSorting(b);
      const result = dateB.getTime() - dateA.getTime(); // Descending (newest first)
      
      // Debug logging for first few comparisons
      if (comparisonCount < 3) {
        console.log('🔍 Comparing:', {
          a: { title: a.title?.substring(0, 20), date: dateA.toISOString().substring(0, 10) },
          b: { title: b.title?.substring(0, 20), date: dateB.toISOString().substring(0, 10) },
          result: result > 0 ? 'B before A' : 'A before B'
        });
        comparisonCount++;
      }
      
      return result;
    });
    
    if (sorted.length > 0) {
      console.log('📅 Final sorted order (should be newest first):', sorted.map((p, index) => ({
        position: index + 1,
        title: p.title?.substring(0, 25) + '...',
        originalDate: p.submissionDate || p.approvalDate || p.planDate || 'No date field',
        parsedDate: getDateForSorting(p).toISOString().substring(0, 10),
        isOldFallback: getDateForSorting(p).getTime() === new Date(1900, 0, 1).getTime()
      })));
    }
    
    return sorted;
  };

  // Filter results into municipal, priority and other categories
  const categorizePlans = (plans) => {
    const telAviv = [];
    const telAvivKlal = [];
    const telAvivLocal = []; // Was telAvivOutline
    const rehovot = [];
    const jerusalem = [];
    const complotPlans = {}; // Dynamic categories for Complot municipality PLANS
    const complotPermits = {}; // Dynamic categories for Complot municipality PERMITS (separate like Tel Aviv)
    const government = []; // Renamed from "other"

    plans.forEach(plan => {
      // Tel Aviv outline/city building plans (layer 528) go to their own section
      if (plan.isTelAvivOutlinePlan) {
        // Robust check for "Klal" (general/city-wide) plans vs Local plans
        const layerName = plan.layerName || '';
        const title = plan.title || '';
        
        // Force Klal categorization if planType was set to 'תוכנית כלל עירונית' in the service
        // This catches all layer 683 plans regardless of title/layerName
        const isKlal = plan.isKlal === true ||
          plan.planType === 'תוכנית כלל עירונית' ||
          (layerName && (layerName.includes('כלל') || layerName.toLowerCase().includes('klal'))) ||
          (title && (title.includes('כלל עירוני') || title.includes('מתאר מקומית')));

        if (isKlal) {
          telAvivKlal.push(plan);
        } else {
          telAvivLocal.push(plan);
        }
        return;
      }

      // Tel Aviv permits must stay in permits section
      if (plan.isTelAvivPermit || (plan.isTelAvivMunicipal && (plan.requestNumber || plan.permitNumber))) {
        telAviv.push(plan);
        return;
      }

      // Tel Aviv municipal plans (non-permits) should appear in plans sections
      if (plan.isTelAvivMunicipal && !plan.isTelAvivPermit) {
        telAvivLocal.push(plan);
        return;
      }

      if (plan.isRehovotMunicipal) {
        rehovot.push(plan);
        return;
      }

      if (plan.isJerusalemMunicipal) {
        jerusalem.push(plan);
        return;
      }

      // Complot PERMITS - separate section per municipality (like Tel Aviv)
      if (plan.isComplotPermit && plan.municipality) {
        const municipalityName = plan.municipality;
        if (!complotPermits[municipalityName]) {
          complotPermits[municipalityName] = [];
        }
        complotPermits[municipalityName].push(plan);
        return;
      }

      // Complot PLANS - separate section per municipality
      if ((plan.isComplotMunicipal || plan.isRamatGanMunicipal) && plan.municipality) {
        const municipalityName = plan.municipality || plan.municipalSource;
        if (!complotPlans[municipalityName]) {
          complotPlans[municipalityName] = [];
        }
        complotPlans[municipalityName].push(plan);
        return;
      }

      // Everything else goes to government plans
      government.push(plan);
    });

    // Sort each Complot municipality category by date
    const sortedComplotPlans = {};
    Object.keys(complotPlans).forEach(key => {
      sortedComplotPlans[key] = sortPlansByDate(complotPlans[key]);
    });
    
    const sortedComplotPermits = {};
    Object.keys(complotPermits).forEach(key => {
      sortedComplotPermits[key] = sortPlansByDate(complotPermits[key]);
    });

    // Sort each category by date (newest first)
    return { 
      telAviv: sortPlansByDate(telAviv), 
      telAvivKlal: sortPlansByDate(telAvivKlal),
      telAvivLocal: sortPlansByDate(telAvivLocal),
      rehovot: sortPlansByDate(rehovot), 
      jerusalem: sortPlansByDate(jerusalem), 
      complotPlans: sortedComplotPlans, // Dynamic municipal PLANS categories
      complotPermits: sortedComplotPermits, // Dynamic municipal PERMITS categories (separate like Tel Aviv)
      government: sortPlansByDate(government) 
    };
  };

  // Helper to extract land use from various sources
  const getLandUseDisplay = () => {
    // Helper to check if land use is meaningful
    const isMeaningfulLandUse = (landUse) => {
      if (!landUse) return false;
      
      // Filter out generic/unhelpful values
      const genericValues = [
        'לא ידוע',
        'יעוד עפ"י תכנית מאושרת אחרת',
        'יעוד עפ״י תכנית מאושרת אחרת'
      ];
      
      if (genericValues.includes(landUse)) return false;
      
      // Filter out master plan's "list of everything" (has multiple commas)
      const commaCount = (landUse.match(/,/g) || []).length;
      if (commaCount > 2) { // More than 2 commas = likely a complete list
        return false;
      }
      
      return true;
    };

    // Debug logging
    const plans = results?.plans || (Array.isArray(results) ? results : []);
    console.log('🔍 Land Use Debug:', {
      hasAdvancedLayers: !!propertyInfo?.advancedLayers,
      advancedLayersKeys: propertyInfo?.advancedLayers ? Object.keys(propertyInfo.advancedLayers) : null,
      advancedLayersLandUse: propertyInfo?.advancedLayers?.landUse,
      advancedLayersLandUseType: typeof propertyInfo?.advancedLayers?.landUse,
      fullAdvancedLayers: propertyInfo?.advancedLayers, // Show the whole object
      hasBuildingData: !!propertyInfo?.buildingData,
      buildingDataLandUse: propertyInfo?.buildingData?.landUse,
      plansCount: plans.length,
      // Show ALL landUse values from plans
      allPlanLandUses: plans.map((p, i) => ({
        index: i,
        planNumber: p.planNumber,
        status: p.status,
        landUse: p.landUse,
        isMeaningful: isMeaningfulLandUse(p.landUse)
      }))
    });

    // 1. PRIORITY: Check GovMap advanced layers (retzefmigrashim) - most accurate!
    if (propertyInfo?.advancedLayers?.landUse?.designation) {
      const landUse = propertyInfo.advancedLayers.landUse.designation;
      console.log('🏛️ Using land use from GovMap retzefmigrashim:', landUse);
      return landUse;
    }

    // 2. Try building data (e.g. Tel Aviv GIS, xplan Layer 4)
    if (propertyInfo?.buildingData?.landUse) {
      const landUse = propertyInfo.buildingData.landUse;
      if (isMeaningfulLandUse(landUse)) {
        console.log('🏛️ Using land use from buildingData:', landUse);
        return landUse;
      } else {
        console.log('⚠️ Building data land use filtered out:', landUse);
      }
    }
    
    // 3. Try to find from plans (National/iPlan) - less strict now
    if (plans && plans.length > 0) {
      // First try: approved detailed plans
      const approvedDetailedPlans = plans.filter(p => 
        (p.status === 'מאושר' || p.statusType === 'approved' || p.status?.includes('אושר')) && 
        p.landUse &&
        isMeaningfulLandUse(p.landUse) &&
        p.planNumber && !p.planNumber.match(/\/\d{1,2}$/) &&
        !p.planNumber.startsWith('תמא') &&
        !p.planNumber.startsWith('תמ')
      );
      
      if (approvedDetailedPlans.length > 0) {
        approvedDetailedPlans.sort((a, b) => {
          const dateA = getDateForSorting(a);
          const dateB = getDateForSorting(b);
          return dateB - dateA;
        });
        console.log('🏛️ Using land use from detailed plan:', approvedDetailedPlans[0].landUse);
        return approvedDetailedPlans[0].landUse;
      }
      
      // Second try: any approved plan with meaningful land use
      const approvedWithLandUse = plans.filter(p => 
        (p.status === 'מאושר' || p.statusType === 'approved' || p.status?.includes('אושר')) && 
        p.landUse &&
        isMeaningfulLandUse(p.landUse)
      );
      
      if (approvedWithLandUse.length > 0) {
        approvedWithLandUse.sort((a, b) => {
          const dateA = getDateForSorting(a);
          const dateB = getDateForSorting(b);
          return dateB - dateA;
        });
        console.log('🏛️ Using land use from approved plan:', approvedWithLandUse[0].landUse);
        return approvedWithLandUse[0].landUse;
      }
      
      // Third try: ANY plan with landUse, even if not approved
      const anyPlanWithLandUse = plans.find(p => 
        p.landUse && isMeaningfulLandUse(p.landUse)
      );
      
      if (anyPlanWithLandUse) {
        console.log('🏛️ Using land use from any plan (not approved):', anyPlanWithLandUse.landUse);
        return anyPlanWithLandUse.landUse;
      }
      
      console.log('⚠️ No plans with meaningful land use found');
    }
    
    console.log('❌ No land use data found from any source');
    return null;
  };

  const { telAviv: telAvivPlans, telAvivKlal: telAvivKlalPlans, telAvivLocal: telAvivLocalPlans, rehovot: rehovotPlans, jerusalem: jerusalemPlans, complotPlans: complotPlansMap, complotPermits: complotPermitsMap, government: governmentPlans } = categorizePlans(normalizedPlans);
  const hasPermits =
    (telAvivPlans && telAvivPlans.length > 0) ||
    (rehovotPlans && rehovotPlans.length > 0) ||
    (jerusalemPlans && jerusalemPlans.length > 0) ||
    (complotPermitsMap && Object.values(complotPermitsMap).some(list => list && list.length > 0));

  // Debug logging for mobile issues
  console.log('🔍 ResultsPage Mobile Debug:', {
    isLoading,
    hasResults: !!results,
    resultsType: Array.isArray(results) ? 'array' : typeof results,
    resultsLength: results?.length || results?.plans?.length,
      priorityPlansLength: 0,
    telAvivPlansLength: telAvivPlans?.length,
    telAvivKlalPlansLength: telAvivKlalPlans?.length,
    telAvivLocalPlansLength: telAvivLocalPlans?.length,
    rehovotPlansLength: rehovotPlans?.length,
    jerusalemPlansLength: jerusalemPlans?.length,
    complotPlansCount: Object.keys(complotPlansMap || {}).length,
    complotPermitsCount: Object.keys(complotPermitsMap || {}).length,
    governmentPlansLength: governmentPlans?.length,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.substring(0, 100) : 'unknown'
  });

  // Helper to handle table sorting
  const handleTableSort = (sectionType, column) => {
    setTableSortConfig(prev => {
      const currentSort = prev[sectionType] || {};
      const isAsc = currentSort.column === column && currentSort.direction === 'asc';
      return {
        ...prev,
        [sectionType]: { column, direction: isAsc ? 'desc' : 'asc' }
      };
    });
  };

  // Helper to handle table filtering
  const handleTableFilter = (sectionType, value) => {
    setTableFilters(prev => ({
      ...prev,
      [sectionType]: value
    }));
  };

  // Render function for result tables (restored)
  const renderPlansTable = (plans, sectionType) => {
    if (!plans || plans.length === 0) return null;

    // Filter plans
    const filterText = tableFilters[sectionType]?.toLowerCase() || '';
    const filteredPlans = plans.filter(plan => {
      if (!filterText) return true;
      const searchText = Object.values(plan).join(' ').toLowerCase();
      return searchText.includes(filterText);
    });

    // Sort plans
    const sortConfig = tableSortConfig[sectionType];
    const getCellValue = (plan, key) => {
      if (key === 'objectives') return getPlanObjectivesWithHydration(plan);
      if (sectionType === 'tel-aviv' && key === 'requestNumber') {
        return plan.requestNumber || plan.planNumber;
      }
      return plan[key];
    };
    const sortedPlans = [...filteredPlans].sort((a, b) => {
      if (!sortConfig) return 0;
      
      const rawA = getCellValue(a, sortConfig.column) ?? '';
      const rawB = getCellValue(b, sortConfig.column) ?? '';

      // Try parsing as dates (DD/MM/YYYY, DD.MM.YYYY, YYYY-MM-DD, or ISO)
      const dateKeys = ['date', 'approvalDate', 'expiryDate', 'submissionDate', 'createdDate'];
      const isDateCol = dateKeys.includes(sortConfig.column) || sortConfig.column.toLowerCase().includes('date') || sortConfig.column.toLowerCase().includes('תאריך');
      
      let cmp;
      if (isDateCol) {
        const parseDate = (v) => {
          if (!v) return 0;
          const s = String(v).trim();
          // DD/MM/YYYY or DD.MM.YYYY
          const dmyMatch = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})/);
          if (dmyMatch) return new Date(+dmyMatch[3], +dmyMatch[2] - 1, +dmyMatch[1]).getTime();
          // ISO or YYYY-MM-DD
          const ts = Date.parse(s);
          return isNaN(ts) ? 0 : ts;
        };
        cmp = parseDate(rawA) - parseDate(rawB);
      } else {
        const numA = Number(rawA);
        const numB = Number(rawB);
        if (!isNaN(numA) && !isNaN(numB) && rawA !== '' && rawB !== '') {
          cmp = numA - numB;
        } else {
          cmp = String(rawA).localeCompare(String(rawB), 'he');
        }
      }
      
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    // Define columns based on section type
    let columns = [];
    
    if (sectionType === 'tel-aviv') {
      columns = [
        { key: 'title', label: 'שם ההיתר' },
        { key: 'requestNumber', label: 'מספר בקשה' },
        { key: 'permitNumber', label: 'מספר היתר' },
        { key: 'requestType', label: 'סוג הבקשה' },
        { key: 'status', label: 'שלב/סטטוס' },
        { key: 'approvalDate', label: 'תאריך היתר' },
        { key: 'expiryDate', label: 'תאריך תפוגה' },
        { key: 'licensingTrack', label: 'מסלול רישוי' },
        { key: 'content', label: 'תוכן הבקשה', isLongText: true, sortable: false }
      ];
    } else if (sectionType === 'rehovot') {
      columns = [
        { key: 'title', label: 'שם הבקשה' },
        { key: 'requestNumber', label: 'מספר בקשה' },
        { key: 'buildingFile', label: 'תיק בניין' },
        { key: 'submissionDate', label: 'תאריך הגשה' },
        { key: 'applicant', label: 'מבקש' },
        { key: 'address', label: 'כתובת' },
        { key: 'requestType', label: 'סוג בקשה' },
        { key: 'housingUnits', label: 'יח"ד' },
        { key: 'status', label: 'סטטוס' }
      ];
    } else if (sectionType === 'jerusalem') {
      columns = [
        { key: 'title', label: 'שם התיק' },
        { key: 'jerusalemTikNum', label: 'מספר תיק' },
        { key: 'requestType', label: 'סוג בקשה' },
        { key: 'description', label: 'תאור', isLongText: true, sortable: false },
        { key: 'requestEssence', label: 'מהות' },
        { key: 'date', label: 'תאריך יצירה' },
        { key: 'status', label: 'סטטוס' }
      ];
    } else if (sectionType.startsWith('complot-permits-')) {
      columns = [
        { key: 'title', label: 'שם הבקשה' },
        { key: 'requestNumber', label: 'מספר בקשה' },
        { key: 'buildingFileNumber', label: 'תיק בניין' },
        { key: 'submissionDate', label: 'תאריך הגשה' },
        { key: 'applicantName', label: 'מבקש' },
        { key: 'address', label: 'כתובת' },
        { key: 'gush', label: 'גוש' },
        { key: 'helka', label: 'חלקה' },
        { key: 'status', label: 'סטטוס' }
      ];
    } else if (sectionType.startsWith('complot-plans-')) {
      columns = [
        { key: 'title', label: 'שם התוכנית' },
        { key: 'planNumber', label: 'מספר תוכנית' },
        { key: 'status', label: 'סטטוס' },
        { key: 'submissionDate', label: 'תאריך' },
        { key: 'planType', label: 'סוג תוכנית' },
        { key: 'county', label: 'רשות מקומית' }
      ];
    } else {
      columns = [
        { key: 'title', label: 'שם התוכנית' },
        { key: 'planNumber', label: 'מספר התוכנית' },
        { key: 'approvingAuthority', label: 'הסמכות המאשרת' },
        { key: 'location', label: 'מיקום' },
        { key: 'status', label: 'סטטוס' },
        { key: 'approvalDate', label: 'תאריך אישור' },
        { key: 'planType', label: 'סוג התוכנית' },
        { key: 'county', label: 'רשות מקומית' },
        { key: 'canIssuePermits', label: 'ניתן להוציא היתרים' },
        { key: 'objectives', label: 'מטרות התוכנית', isLongText: true, sortable: false }
      ];

      // Keep Klal table compact (similar visual density to municipal permits table)
      // to avoid horizontal scrolling.
      if (sectionType === 'tel-aviv-klal') {
        columns = [
          { key: 'title', label: 'שם התוכנית' },
          { key: 'planNumber', label: 'מספר התוכנית' },
          { key: 'status', label: 'סטטוס' },
          { key: 'approvalDate', label: 'תאריך אישור' },
          { key: 'planType', label: 'סוג התוכנית' },
          { key: 'county', label: 'רשות מקומית' },
          { key: 'canIssuePermits', label: 'ניתן להוציא היתרים' },
          { key: 'objectives', label: 'מטרות התוכנית', isLongText: true, sortable: false }
        ];
      }
    }

    // Helper to toggle expanded text
    const toggleText = (id) => {
      setExpandedObjectives(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
    };

    return (
      <div className="plans-table-container">
        <div className="plans-table-header">
          <div className="search-wrapper">
            <i className="fi fi-rr-search search-icon"></i>
            <input 
              id={`plans-filter-${sectionType}`}
              name={`plans_filter_${sectionType}`}
              type="text" 
              autoComplete="off"
              className="plans-filter-input" 
              placeholder="סינון לפי טקסט..." 
              value={tableFilters[sectionType] || ''}
              onChange={(e) => handleTableFilter(sectionType, e.target.value)}
            />
          </div>
        </div>
        
        <div className="plans-table-wrapper">
          <table className="plans-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {columns.map(col => (
                  <th 
                    key={col.key} 
                    className={col.sortable !== false ? 'sortable' : ''}
                    onClick={() => col.sortable !== false && handleTableSort(sectionType, col.key)}
                    style={{
                      padding: '12px',
                      textAlign: 'right',
                      cursor: col.sortable !== false ? 'pointer' : 'default',
                      whiteSpace: sectionType === 'tel-aviv-klal' ? 'normal' : 'nowrap',
                      lineHeight: sectionType === 'tel-aviv-klal' ? '1.25' : undefined
                    }}
                  >
                    {col.label} {sortConfig?.column === col.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPlans.map((plan, index) => {
                return (
                  <tr 
                    key={index} 
                    onClick={() => {
                      handleViewPlan(plan);
                    }}
                  >
                    {columns.map(col => (
                      <td 
                        key={col.key} 
                        data-label={col.label}
                        style={{
                          padding: '12px',
                          borderTop: '1px solid var(--border-light)',
                          verticalAlign: 'top',
                          wordBreak: sectionType === 'tel-aviv-klal' ? 'break-word' : undefined,
                          whiteSpace: sectionType === 'tel-aviv-klal' ? 'normal' : undefined
                        }}
                      >
                        {(() => {
                          const cellValue = getCellValue(plan, col.key);
                          if (col.key === 'status') {
                            return (
                              <span className={`result-status status-${plan.statusType || plan.status}`}>
                                {cellValue}
                              </span>
                            );
                          }
                          if (col.key === 'canIssuePermits') {
                            return plan[col.key] ? 'כן' : 'לא';
                          }
                          if (col.isLongText) {
                            return (
                              <div className="long-text-cell">
                                <div className={`text-content ${expandedObjectives[`${sectionType}-${index}`] ? 'expanded' : ''}`}>
                                  {cellValue || '-'}
                                </div>
                                {cellValue && cellValue.length > 50 && (
                                  <button 
                                    className="text-toggle-btn"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent row toggle
                                      toggleText(`${sectionType}-${index}`);
                                    }}
                                  >
                                    <span className="toggle-text">{expandedObjectives[`${sectionType}-${index}`] ? 'הצג פחות' : 'הצג עוד'}</span>
                                    <span className={`toggle-arrow ${expandedObjectives[`${sectionType}-${index}`] ? 'expanded' : ''}`}>▼</span>
                                  </button>
                                )}
                              </div>
                            );
                          }
                          const dateKeys = ['date', 'approvalDate', 'expiryDate', 'submissionDate', 'createdDate'];
                          const isDateCol =
                            dateKeys.includes(col.key) ||
                            col.key.toLowerCase().includes('date') ||
                            col.label.includes('תאריך');
                          const displayValue = isDateCol ? formatDisplayDateValue(cellValue) : (cellValue || '-');
                          return (
                            <>
                              {displayValue}
                            </>
                          );
                        })()}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="table-footer" style={{ padding: '8px', fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'left' }}>
          סה"כ: {sortedPlans.length} רשומות
        </div>
      </div>
    );
  }; 

  // NEW: Render plan preview section with preview cards and "show more" button
  const renderPlanPreviewSection = (plans, sectionType, title, icon, isExpanded, setExpanded) => {
    if (!plans || plans.length === 0) return null;
    
    const PREVIEW_COUNT = 3; // Show 3 preview cards
    const previewPlans = plans.slice(0, PREVIEW_COUNT);
    const remainingCount = plans.length - PREVIEW_COUNT;
    
    // Get key fields based on section type
    const getKeyFields = (plan) => {
      if (sectionType === 'tel-aviv') {
        return {
          name: plan.title || 'ללא שם',
          number: plan.requestNumber || plan.planNumber || plan.permitNumber || '-',
          status: plan.status || '-',
          date: plan.approvalDate || plan.expiryDate || '-',
          type: plan.requestType || plan.licensingTrack || '-'
        };
      } else if (sectionType === 'rehovot') {
        return {
          name: plan.title || 'ללא שם',
          number: plan.requestNumber || plan.buildingFile || '-',
          status: plan.status || '-',
          date: plan.submissionDate || '-',
          type: plan.requestType || '-'
        };
      } else if (sectionType === 'jerusalem') {
        return {
          name: plan.title || 'ללא שם',
          number: plan.jerusalemTikNum || '-',
          status: plan.status || '-',
          date: plan.date || '-',
          type: plan.requestType || '-'
        };
      } else if (sectionType.startsWith('complot-permits-')) {
        // Complot municipal PERMITS (Ramat Gan, etc.) - show permit-specific fields
        return {
          name: plan.title || `בקשה ${plan.requestNumber}` || 'ללא שם',
          number: plan.requestNumber || '-',
          status: plan.status || '-',
          date: plan.submissionDate || '-',
          type: plan.applicantName || plan.address || '-'
        };
      } else if (sectionType === 'ramat-gan' || sectionType.startsWith('complot-plans-') || sectionType.startsWith('complot-')) {
        // Complot municipal PLANS (Ramat Gan, etc.)
        return {
          name: plan.title || plan.planNumber || 'ללא שם',
          number: plan.planNumber || '-',
          status: plan.status || '-',
          date: plan.approvalDate || plan.statusDate || plan.submissionDate || '-',
          type: plan.planType || '-'
        };
      } else {
        // Government and others (including tel-aviv-local, tel-aviv-klal)
        return {
          name: plan.title || 'ללא שם',
          number: plan.planNumber || '-',
          status: plan.status || '-',
          date: plan.approvalDate || '-',
          type: plan.planType || plan.approvingAuthority || '-'
        };
      }
    };

    const getStatusClass = (status) => {
      if (!status) return '';
      const s = status.toLowerCase();
      if (s.includes('אושר') || s.includes('approved') || s.includes('בתוקף')) return 'status-approved';
      if (s.includes('מאושר')) return 'status-approved';
      if (s.includes('בטיפול') || s.includes('pending') || s.includes('הפקדה')) return 'status-pending';
      if (s.includes('נדחה') || s.includes('rejected') || s.includes('בוטל')) return 'status-rejected';
      return 'status-default';
    };

    return (
      <div className="plan-preview-section">
        <div className="plan-preview-header">
          <h4 className="plan-preview-title">
            <i className={`fi ${icon}`}></i> {title}
            <span className="plan-count-badge">{plans.length}</span>
          </h4>
        </div>
        
        <div className="plan-preview-cards">
          {previewPlans.map((plan, index) => {
            const fields = getKeyFields(plan);
            return (
              <div
                key={index}
                className="plan-preview-card"
                role="button"
                tabIndex={0}
                onClick={() => handleViewPlan(plan)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewPlan(plan);
                  }
                }}
                aria-label={`פתח פרטי תוכנית ${fields.name}`}
              >
                <div className="preview-card-header">
                  <span className="preview-card-name">{fields.name}</span>
                  <span className={`preview-card-status ${getStatusClass(fields.status)}`}>
                    {fields.status}
                  </span>
                </div>
                <div className="preview-card-details">
                  <div className="preview-detail">
                    <span className="preview-label">מספר:</span>
                    <span className="preview-value">{fields.number}</span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">תאריך:</span>
                    <span className="preview-value">{fields.date}</span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">סוג:</span>
                    <span className="preview-value">{fields.type}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <button 
          className="btn btn-outline btn-sm show-more-plans-btn"
          onClick={() => setExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <i className="fi fi-rr-angle-small-up"></i> הסתר טבלה מפורטת
            </>
          ) : (
            <>
              <i className="fi fi-rr-angle-small-down"></i> {remainingCount > 0 ? `הצג עוד ${remainingCount} תוכניות בטבלה מפורטת` : 'הצג בתצוגת טבלה'}
            </>
          )}
        </button>
        
        {isExpanded && (
          <div className="plan-full-table-wrapper">
            {renderPlansTable(plans, sectionType)}
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const renderResultCard = (result) => (
    <div key={result.id} className={`result-card ${result.isRealData ? 'real-data' : 'mock-data'} ${result.priorityType ? 'priority-plan' : ''} ${(result.isTelAvivMunicipal || result.isTelAvivOutlinePlan) ? 'tel-aviv-municipal' : ''} ${result.isRehovotMunicipal ? 'rehovot-municipal' : ''} ${result.isJerusalemMunicipal ? 'jerusalem-municipal' : ''} ${result.isRamatGanMunicipal ? 'ramat-gan-municipal' : ''} ${result.isComplotMunicipal ? 'complot-municipal' : ''}`}>
      <div className="result-header">
        <h3 className="result-title">{result.title}</h3>
        <div className="result-header-badges">
          {result.priorityType && (
            <span className="priority-badge">
              <i className="fi fi-rr-building"></i> {result.priorityType}
            </span>
          )}
          <span className={`result-status status-${result.statusType || result.status}`}>
            {result.status}
          </span>
          {result.isRealData && (
            <span className={`data-badge real ${(result.isTelAvivMunicipal || result.isTelAvivOutlinePlan) ? 'tel-aviv-municipal' : ''} ${result.isRehovotMunicipal ? 'rehovot-municipal' : ''} ${result.isJerusalemMunicipal ? 'jerusalem-municipal' : ''} ${result.isRamatGanMunicipal ? 'ramat-gan-municipal' : ''} ${result.isComplotMunicipal ? 'complot-municipal' : ''}`}>
              {(result.isTelAvivMunicipal || result.isTelAvivOutlinePlan) ? <><i className="fi fi-rr-bank"></i> עיריית תל אביב</> : 
               result.isRehovotMunicipal ? <><i className="fi fi-rr-building"></i> עיריית רחובות</> : 
               result.isJerusalemMunicipal ? <><i className="fi fi-rr-landmark"></i> עיריית ירושלים</> :
               result.isRamatGanMunicipal ? <><i className="fi fi-rr-city"></i> עיריית רמת גן</> :
               result.isComplotMunicipal ? <><i className="fi fi-rr-city"></i> עיריית {result.municipalSource}</> : <><i className="fi fi-rr-check"></i> נתונים רשמיים</>}
            </span>
          )}
        </div>
      </div>

      <div className="result-details">
        {result.isTelAvivMunicipal || result.isTelAvivOutlinePlan ? (
          // Tel Aviv municipal data
          <>
            {(result.requestNumber || result.planNumber) && (
              <div className="detail-item">
                <span className="detail-label">{result.isTelAvivPermit ? 'מספר בקשה' : 'מספר תוכנית'}</span>
                <span className="detail-value">{result.isTelAvivPermit ? (result.requestNumber || result.planNumber) : result.planNumber}</span>
              </div>
            )}
            {result.permitNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר היתר</span>
                <span className="detail-value">{result.permitNumber}</span>
              </div>
            )}
            {result.submissionDate && (
              <div className="detail-item">
                <span className="detail-label">{result.isTelAvivPermit ? 'תאריך פתיחת בקשה' : 'תאריך הפקדה'}</span>
                <span className="detail-value">{result.submissionDate}</span>
              </div>
            )}
            {result.approvalDate && (
              <div className="detail-item">
                <span className="detail-label">תאריך אישור</span>
                <span className="detail-value">{result.approvalDate}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">סוג</span>
              <span className="detail-value">{result.planType}</span>
            </div>
            {result.address && (
              <div className="detail-item">
                <span className="detail-label">כתובת</span>
                <span className="detail-value">{result.address}</span>
              </div>
            )}
            {result.floors && (
              <div className="detail-item">
                <span className="detail-label">קומות</span>
                <span className="detail-value">{result.floors}</span>
              </div>
            )}
            {result.height && (
              <div className="detail-item">
                <span className="detail-label">גובה</span>
                <span className="detail-value">{result.height}</span>
              </div>
            )}
            {result.area && (
              <div className="detail-item">
                <span className="detail-label">שטח</span>
                <span className="detail-value">{result.area}</span>
              </div>
            )}
          </>
        ) : result.isRehovotMunicipal ? (
          // Rehovot municipal data
          <>
            {result.requestNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר בקשה</span>
                <span className="detail-value">{result.requestNumber}</span>
              </div>
            )}
            {result.buildingFile && (
              <div className="detail-item">
                <span className="detail-label">תיק בניין</span>
                <span className="detail-value">{result.buildingFile}</span>
              </div>
            )}
            {result.submissionDate && (
              <div className="detail-item">
                <span className="detail-label">תאריך הגשה</span>
                <span className="detail-value">{result.submissionDate}</span>
              </div>
            )}
            {result.applicant && (
              <div className="detail-item">
                <span className="detail-label">מבקש</span>
                <span className="detail-value">{result.applicant}</span>
              </div>
            )}
            {result.address && (
              <div className="detail-item">
                <span className="detail-label">כתובת</span>
                <span className="detail-value">{result.address}</span>
              </div>
            )}
            {result.requestType && (
              <div className="detail-item">
                <span className="detail-label">סוג הבקשה</span>
                <span className="detail-value">{result.requestType}</span>
              </div>
            )}
            {result.housingUnits && (
              <div className="detail-item">
                <span className="detail-label">יחידות דיור</span>
                <span className="detail-value">{result.housingUnits}</span>
              </div>
            )}
          </>
        ) : result.isJerusalemMunicipal ? (
          // Jerusalem municipal data
          <>
            {result.jerusalemTikNum && (
              <div className="detail-item">
                <span className="detail-label">מספר תיק</span>
                <span className="detail-value">{result.jerusalemTikNum}</span>
              </div>
            )}
            {result.requestType && (
              <div className="detail-item">
                <span className="detail-label">סוג בקשה</span>
                <span className="detail-value">{result.requestType}</span>
              </div>
            )}
            {result.description && (
              <div className="detail-item">
                <span className="detail-label">תאור הבקשה</span>
                <span className="detail-value">{result.description}</span>
              </div>
            )}
            {result.requestEssence && (
              <div className="detail-item">
                <span className="detail-label">מהות בקשה</span>
                <span className="detail-value">{result.requestEssence}</span>
              </div>
            )}
            {result.date && (
              <div className="detail-item">
                <span className="detail-label">תאריך יצירה</span>
                <span className="detail-value">{result.date}</span>
              </div>
            )}
            {result.statusDate && (
              <div className="detail-item">
                <span className="detail-label">תאריך סטטוס</span>
                <span className="detail-value">{result.statusDate}</span>
              </div>
            )}
            {result.applicant && (
              <div className="detail-item">
                <span className="detail-label">מבקש</span>
                <span className="detail-value">{result.applicant}</span>
              </div>
            )}
            {result.streetName && (
              <div className="detail-item">
                <span className="detail-label">רחוב</span>
                <span className="detail-value">{result.streetName}</span>
              </div>
            )}
            {result.buildingNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר בית</span>
                <span className="detail-value">{result.buildingNumber}</span>
              </div>
            )}
            {result.neighborhood && (
              <div className="detail-item">
                <span className="detail-label">שכונה</span>
                <span className="detail-value">{result.neighborhood}</span>
              </div>
            )}
            {result.area && (
              <div className="detail-item">
                <span className="detail-label">שטח אתר (מ"ר)</span>
                <span className="detail-value">{result.area}</span>
              </div>
            )}
            {result.usage && (
              <div className="detail-item">
                <span className="detail-label">שימוש המבנה</span>
                <span className="detail-value">{result.usage}</span>
              </div>
            )}
            {result.location && (
              <div className="detail-item">
                <span className="detail-label">מיקום באזור</span>
                <span className="detail-value">{result.location}</span>
              </div>
            )}
            {result.department && (
              <div className="detail-item">
                <span className="detail-label">צוות מטפל</span>
                <span className="detail-value">{result.department}</span>
              </div>
            )}
            {result.contractor && (
              <div className="detail-item">
                <span className="detail-label">קבלן</span>
                <span className="detail-value">{result.contractor}</span>
              </div>
            )}
            {result.architect && (
              <div className="detail-item">
                <span className="detail-label">אחראי/אדריכל</span>
                <span className="detail-value">{result.architect}</span>
              </div>
            )}
            {result.permitStatus && (
              <div className="detail-item">
                <span className="detail-label">סטטוס רישוי</span>
                <span className="detail-value">{result.permitStatus}</span>
              </div>
            )}
            {result.detailedDescription && (
              <div className="detail-item">
                <span className="detail-label">תיאור מפורט</span>
                <span className="detail-value" dangerouslySetInnerHTML={{__html: result.detailedDescription.substring(0, 200) + (result.detailedDescription.length > 200 ? '...' : '')}}></span>
              </div>
            )}
            {result.floors && (
              <div className="detail-item">
                <span className="detail-label">קומות</span>
                <span className="detail-value">{result.floors}</span>
              </div>
            )}
            {result.apartments && (
              <div className="detail-item">
                <span className="detail-label">דירות</span>
                <span className="detail-value">{result.apartments}</span>
              </div>
            )}
            {/* Debug info temporarily hidden
            {result.detailsResponse && (
              <div className="detail-item" style={{backgroundColor: '#f9f9f9', border: '1px solid #ddd', padding: '8px', marginTop: '10px'}}>
                <span className="detail-label" style={{fontWeight: 'bold', color: '#d4a914'}}>🕌 מידע מפורט</span>
                <span className="detail-value" style={{fontSize: '11px', wordWrap: 'break-word'}}>{result.detailsResponse ? JSON.stringify(result.detailsResponse, null, 2) : 'לא זמין'}</span>
              </div>
            )}
            */}
          </>
        ) : result.isRamatGanMunicipal ? (
          // Ramat Gan municipal data
          <>
            {result.planNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר תוכנית</span>
                <span className="detail-value">{result.planNumber}</span>
              </div>
            )}
            {result.status && (
              <div className="detail-item">
                <span className="detail-label">סטטוס</span>
                <span className="detail-value">{result.status}</span>
              </div>
            )}
            {result.submissionDate && (
              <div className="detail-item">
                <span className="detail-label">תאריך</span>
                <span className="detail-value">{result.submissionDate}</span>
              </div>
            )}
            {result.description && result.description !== result.title && (
              <div className="detail-item">
                <span className="detail-label">תיאור</span>
                <span className="detail-value">{result.description}</span>
              </div>
            )}
          </>
        ) : result.isComplotMunicipal ? (
          // Other Complot municipalities (Haifa, Bat Yam, Hod HaSharon, etc.)
          <>
            {result.planNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר תוכנית</span>
                <span className="detail-value">{result.planNumber}</span>
              </div>
            )}
            {result.status && (
              <div className="detail-item">
                <span className="detail-label">סטטוס</span>
                <span className="detail-value">{result.status}</span>
              </div>
            )}
            {(result.submissionDate || result.statusDate) && (
              <div className="detail-item">
                <span className="detail-label">תאריך</span>
                <span className="detail-value">{result.submissionDate || result.statusDate}</span>
              </div>
            )}
            {result.municipalSource && (
              <div className="detail-item">
                <span className="detail-label">עירייה</span>
                <span className="detail-value">{result.municipalSource}</span>
              </div>
            )}
            {result.description && result.description !== result.title && (
              <div className="detail-item">
                <span className="detail-label">תיאור</span>
                <span className="detail-value">{result.description}</span>
              </div>
            )}
          </>
        ) : result.isComplotPermit ? (
          // Complot building permits (היתרי בנייה)
          <>
            {result.requestNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר בקשה</span>
                <span className="detail-value">{result.requestNumber}</span>
              </div>
            )}
            {result.buildingFileNumber && (
              <div className="detail-item">
                <span className="detail-label">תיק בניין</span>
                <span className="detail-value">{result.buildingFileNumber}</span>
              </div>
            )}
            {result.submissionDate && (
              <div className="detail-item">
                <span className="detail-label">תאריך הגשה</span>
                <span className="detail-value">{result.submissionDate}</span>
              </div>
            )}
            {result.applicantName && (
              <div className="detail-item">
                <span className="detail-label">שם המבקש</span>
                <span className="detail-value">{result.applicantName}</span>
              </div>
            )}
            {result.address && (
              <div className="detail-item">
                <span className="detail-label">כתובת</span>
                <span className="detail-value">{result.address}</span>
              </div>
            )}
            {result.municipality && (
              <div className="detail-item">
                <span className="detail-label">עירייה</span>
                <span className="detail-value">{result.municipality}</span>
              </div>
            )}
          </>
        ) : result.isRealData ? (
          // Regular real architectural plan data
          <>
            {result.planNumber && (
              <div className="detail-item">
                <span className="detail-label">מספר תוכנית</span>
                <span className="detail-value">{result.planNumber}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">תאריך פרסום</span>
              <span className="detail-value">{result.submissionDate}</span>
            </div>
            {result.approvalDate && result.approvalDate !== 'לא ידוע' && (
              <div className="detail-item">
                <span className="detail-label">תאריך אישור</span>
                <span className="detail-value">{result.approvalDate}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">סוג תוכנית</span>
              <span className="detail-value">{result.planType}</span>
            </div>
            {result.area && (
              <div className="detail-item">
                <span className="detail-label">שטח</span>
                <span className="detail-value">{result.area}</span>
              </div>
            )}
            {result.county && (
              <div className="detail-item">
                <span className="detail-label">רשות מקומית</span>
                <span className="detail-value">{result.county}</span>
              </div>
            )}
            {result.objectives && (
              <div className="detail-item">
                <span className="detail-label">מטרות התוכנית</span>
                <span className="detail-value" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{result.objectives}</span>
              </div>
            )}
            {result.planSubtype && (
              <div className="detail-item">
                <span className="detail-label">תת-סוג תוכנית</span>
                <span className="detail-value">{result.planSubtype}</span>
              </div>
            )}
            {result.permitType && (
              <div className="detail-item">
                <span className="detail-label">סוג היתר</span>
                <span className="detail-value">{result.permitType}</span>
              </div>
            )}
          </>
        ) : (
          // Mock data (existing structure)
          <>
            <div className="detail-item">
              <span className="detail-label">תאריך הגשה</span>
              <span className="detail-value">{result.submissionDate}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">אדריכל</span>
              <span className="detail-value">{result.architect}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">סוג תוכנית</span>
              <span className="detail-value">{result.planType}</span>
            </div>
            {result.floor && result.apartment && (
              <div className="detail-item">
                <span className="detail-label">מיקום</span>
                <span className="detail-value">{result.floor}, {result.apartment}</span>
              </div>
            )}
            {result.area && (
              <div className="detail-item">
                <span className="detail-label">שטח</span>
                <span className="detail-value">{result.area}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="result-actions">
        <button 
          className="btn btn-primary btn-sm single-action"
          onClick={() => handleViewPlan(result)}
        >
          {(result.isTelAvivMunicipal || result.isTelAvivOutlinePlan) ? 'צפה באתר עיריית תל אביב' : 
           result.isRehovotMunicipal ? 'צפה באתר עיריית רחובות' :
           result.isJerusalemMunicipal ? 'צפה באתר עיריית ירושלים' :
           result.isRamatGanMunicipal ? 'צפה באתר עיריית רמת גן' :
           result.isComplotMunicipal ? `צפה באתר עיריית ${result.municipalSource}` :
           result.isRealData ? 'פתח במערכת הממשלתית' : 'פרטים נוספים'}
        </button>
      </div>
    </div>
  );

  // Show shimmer effect while loading
  if (isLoading && !results) {
    return <ResultsPageShimmer />;
  }

  const limitedDataWarningJSX = sourceHealth?.criticalSourceUnavailable ? (
    <div className="limited-data-warning" role="status" aria-live="polite">
      <span className="limited-data-warning-icon">
        <i className="fi fi-rr-triangle-warning"></i>
      </span>
      <div className="limited-data-warning-copy">
        <h3>חלק ממקורות המידע אינם זמינים כרגע</h3>
        <p>{getLimitedDataWarningText(sourceHealth)}</p>
        <p className="limited-data-warning-note">דוח ה־AI לא יוצג במצב הזה כדי לא לייצר המלצה על בסיס מידע חסר.</p>
      </div>
    </div>
  ) : null;

  const updatesNotificationFormJSX = (
    <div id="property-alerts-signup" className="updates-notification-form updates-notification-form-prominent">
      <div className="updates-form-header">
        <span className="updates-icon"><i className="fi fi-rr-bell"></i></span>
        <div className="updates-heading-copy">
          <p className="updates-description">קבלת התראות על שינויים בנכס</p>
          <p className="updates-subdescription">נעדכן במייל כשמתווספת תכנית או בקשת היתר חדשה שרלוונטית לנכס הזה.</p>
        </div>
      </div>
      {updatesSuccess ? (
        <div className="updates-success-message">
          <h4>
            <i className="fi fi-rr-check-circle"></i>
            <span>נרשמת בהצלחה!</span>
          </h4>
          <p>אנו נעדכן אותך במייל על כל שינוי בסטטוס התכנוני של הנכס.</p>
        </div>
      ) : (
        <form className="updates-form" onSubmit={handleUpdatesSubmit} noValidate>
          <div className="updates-property-context" aria-label="הנכס להרשמה">
            <span className="updates-property-label">הנכס למעקב</span>
            <span className="updates-property-address">{alertPropertyLabel}</span>
          </div>
          <div className="updates-field">
            <input 
              id="updates-email"
              name="email"
              type="email" 
              autoComplete="email"
              placeholder="אימייל"
              className={`updates-input ${updatesErrors.email ? 'input-error' : ''}`}
              value={updatesEmail}
              onFocus={() => {
                if (alertsSignupStartedRef.current) return;
                alertsSignupStartedRef.current = true;
                AnalyticsService.trackAlertsSignupStarted({
                  municipality: propertyInfo?.municipality || propertyInfo?.municipalityName || results?.municipality,
                  searchMode: isParcelSearch ? 'parcel' : 'address',
                  sourceSurface: 'results_page',
                });
              }}
              onChange={(e) => {
                setUpdatesEmail(e.target.value);
                if (updatesErrors.email) setUpdatesErrors(prev => ({...prev, email: ''}));
              }}
            />
            {updatesErrors.email && <span className="error-message">{updatesErrors.email}</span>}
          </div>
          <button type="submit" className={`btn btn-primary updates-submit-btn ${isSubmittingUpdates ? 'is-loading' : ''}`} disabled={isSubmittingUpdates}>
            {isSubmittingUpdates ? (
              <>
                <span className="spinner-small"></span> שולח...
              </>
            ) : (
              'הפעלת התראות'
            )}
          </button>
          {updatesErrors.submit && <div className="error-message updates-submit-error">{updatesErrors.submit}</div>}
        </form>
      )}
    </div>
  );

  // Embedded mode: just return the results content without app wrapper
  if (embedded) {
    return (
      <div className="results-container">
        <SectionNav />
        <div className="results-header" ref={resultsTopRef}>
        {results && results.length > 0 && (
            <div className="results-summary">
              <p className="summary-text">
                נמצאו <strong>{results.length}</strong> תוכניות אדריכליות
              </p>
            </div>
          )}
        </div>

        {/* API Error Messages - Global */}
        {Object.keys(apiErrors).length > 0 && !sourceHealth?.criticalSourceUnavailable && (
          <div className="api-errors-container">
            {apiErrors.critical && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-exclamation"></i> שגיאת מערכת</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.critical}</p>
                </div>
                <div className="result-actions">
                  <button className="btn btn-primary" onClick={() => window.location.reload()}>
                    <i className="fi fi-rr-refresh"></i> נסה שוב
                  </button>
                </div>
              </div>
            )}
            
            {apiErrors.propertyInfo && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-home"></i> מידע קרקע</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.propertyInfo}</p>
                </div>
                <div className="result-actions">
                  <button className="btn btn-primary" onClick={onRetryPropertyInfo}>
                    🔄 נסה להתחבר שוב
                  </button>
                </div>
              </div>
            )}
            
            {apiErrors.plans && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-clipboard"></i> תוכניות בינוי</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.plans}</p>
                </div>
                <div className="result-actions">
                  {onRetryPlans && (
                    <button className="btn btn-primary" onClick={onRetryPlans}>
                      🔄 נסה להתחבר שוב
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Property & Plans Information Section */}
        {(activeTab === 'property' || activeTab === 'feasibility') && (
        <>
        {limitedDataWarningJSX}
        {updatesNotificationFormJSX}
        <div className="property-section-wrapper">
          
          <div className="property-tab-layout">
            <div className="property-info-column">
              {/* Unified Data Summary Card - Merged ריכוז נתונים & נתוני נכס */}
              {propertyInfo && (
              <>
              <div id="section-summary" className="market-data-card real-data property-summary-card">
                {/* Section Header - Same style as נתוני שוק ומחירים */}
                <div className="market-data-header">
                  <h3 className="market-data-title"><i className="fi fi-rr-file-invoice"></i> ריכוז נתונים</h3>
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{formatDate(searchTimestamp)} {formatTime(searchTimestamp)}</span>
                </div>
                
                <div className="market-data-content">
                {/* Summary Text */}
                <div className="data-item summary-text-item">
                  <p className="summary-main-text">
                    המערכת בדקה <strong>{getSourcesCount()} מקורות רשמיים</strong> וריכזה עבורך את המידע העדכני ביותר.
                  </p>
                </div>

                {/* Divider after summary text */}
                <div style={{ margin: '2px 0', borderBottom: '1px solid var(--border-light)', gridColumn: '1 / -1' }}></div>

              {/* Property Identification */}
              <div className="data-item">
                <span className="data-label">גוש:</span>
                <span className="data-value">{propertyInfo?.gush || 'לא זוהה'}</span>
                <p className="data-description">מספר הגוש במרשם המקרקעין.</p>
              </div>
              
              <div className="data-item">
                <span className="data-label">חלקה:</span>
                <span className="data-value">{propertyInfo?.helka || 'לא זוהתה'}</span>
                <p className="data-description">מספר החלקה במרשם המקרקעין.</p>
              </div>
              
              {propertyInfo?.subHelka && (
                <div className="data-item">
                  <span className="data-label">תת-חלקה:</span>
                  <span className="data-value">{propertyInfo.subHelka}</span>
                  <p className="data-description">מספר תת-חלקה במרשם המקרקעין.</p>
                </div>
              )}
              
              <div className="data-item">
                <span className="data-label">רשות מקומית:</span>
                <span className="data-value">{propertyInfo?.municipality || 'לא זוהתה רשות מקומית'}</span>
                <p className="data-description">הרשות המקומית בה ממוקם הנכס.</p>
              </div>
              
              {displayPropertyAddress && (
                <div className="data-item">
                  <span className="data-label">כתובת:</span>
                  <span className="data-value">{displayPropertyAddress}</span>
                  <p className="data-description">הכתובת המלאה של הנכס.</p>
                </div>
              )}
              
              {getLandUseDisplay() && (
                <div className="data-item">
                  <span className="data-label">ייעוד קרקע:</span>
                  <span className="data-value">{getLandUseDisplay()}</span>
                  <p className="data-description">ייעוד הקרקע לפי תוכניות מאושרות.</p>
                </div>
              )}

              {/* Municipal Building Data (if available) */}
              {propertyInfo.buildingData && (
                <>
              {propertyInfo.buildingData.buildingNumber && (
                <div className="data-item">
                  <span className="data-label">מספר מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.buildingNumber}</span>
                  <p className="data-description">מספר הזיהוי הפנימי של הבניין במערכות העירוניות.</p>
                </div>
              )}
              {(propertyInfo.buildingData.floors || effectiveBuildingData?.floors) && (
                <div className="data-item">
                  <span className="data-label">מספר קומות:</span>
                  <span className="data-value">{propertyInfo.buildingData.floors || effectiveBuildingData.floors}</span>
                  {effectiveBuildingData?.floorsOverrideReason ? (
                    <p className="data-description" style={{ color: 'var(--status-warning-text)' }}>⚠️ {effectiveBuildingData.floorsOverrideReason}</p>
                  ) : (
                    <p className="data-description">כמות הקומות הרשומה לבניין.</p>
                  )}
                </div>
              )}
              {propertyInfo.buildingData.height && (
                <div className="data-item">
                  <span className="data-label">גובה מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.height} מ׳</span>
                  <p className="data-description">הגובה הרשום, אינדיקטיבי.</p>
                </div>
              )}
              {propertyInfo.buildingData.area && (
                <div className="data-item">
                  <span className="data-label">שטח חלקה:</span>
                  <span className="data-value">{propertyInfo.buildingData.area} מ״ר</span>
                  <p className="data-description">השטח המדוד/רשום של החלקה.</p>
                </div>
              )}
              {effectiveBuildingData?.year && !effectiveBuildingData?.yearEstimated && (
                <div className="data-item">
                  <span className="data-label">שנת בנייה:</span>
                  <span className="data-value">{String(effectiveBuildingData.year) === '1900' ? 'לא ידוע' : effectiveBuildingData.year}</span>
                  <p className="data-description">
                    {effectiveBuildingData.yearNote || 'השנה בה נרשמה בניית המבנה/טופס אכלוס.'}
                  </p>
                </div>
              )}
              {propertyInfo.buildingData.buildingType && (
                <div className="data-item">
                  <span className="data-label">סוג מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.buildingType}</span>
                  <p className="data-description">סיווג טיפוסי (למשל מגורים/מעורב/ציבורי).</p>
                </div>
              )}
              {propertyInfo.buildingData.hasColumns && (
                <div className="data-item">
                  <span className="data-label">עמודים:</span>
                  <span className="data-value">{propertyInfo.buildingData.hasColumns}</span>
                  <p className="data-description">האם קומת העמודים פתוחה/קיימת לפי הרישום.</p>
                </div>
              )}
              {propertyInfo.buildingData.asbestLevel && propertyInfo.buildingData.asbestLevel !== 'לא ידוע' && (
                <div className="data-item">
                  <span className="data-label">סכנת אסבסט:</span>
                  <span className="data-value">{propertyInfo.buildingData.asbestLevel}</span>
                  <p className="data-description">אינדיקציה עירונית לנוכחות אסבסט/חשש.</p>
                </div>
              )}
              {propertyInfo.buildingData.conservationSite?.isConservationSite && (
                <div className="data-item">
                  <span className="data-label">שימור:</span>
                  <span className="data-value">{propertyInfo.buildingData.conservationSite.designation || 'מבנה לשימור'}</span>
                  <p className="data-description">מבנה/אתר מסומן לשימור לפי GIS עיריית תל אביב.</p>
                </div>
              )}
              {!propertyInfo.buildingData.conservationSite?.isConservationSite && propertyInfo.buildingData.conservationArea?.inConservationZone && (
                <div className="data-item">
                  <span className="data-label">שימור (אזור):</span>
                  <span className="data-value">{propertyInfo.buildingData.conservationArea.layerName || 'אזור שימור'}</span>
                  <p className="data-description">
                    {propertyInfo.buildingData.conservationArea.areaName
                      ? `אזור: ${propertyInfo.buildingData.conservationArea.areaName}`
                      : 'הנכס נמצא באזור אחריות שימור.'}
                  </p>
                </div>
              )}
                </>
              )}

              {/* GovMap Extended Data - National Data */}
              <GovMapData 
                coordinates={propertyInfo?.coordinates || results?.coordinates}
                propertyInfo={propertyInfo}
              />
              
              {/* Divider before tik binyan */}
              {municipality && (
                <div style={{ margin: '2px 0', borderBottom: '1px solid var(--border-light)', gridColumn: '1 / -1' }}></div>
              )}
              
              {/* Building File (Tik Binyan) */}
              {municipality && (
              <div style={{ 
                gridColumn: '1 / -1'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="fi fi-rr-folder-open" style={{ color: 'var(--color-cerulean)', fontSize: '18px' }}></i>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>תיק בניין</span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 10px 0', lineHeight: '1.5' }}>
                      תיק הבניין מכיל את כל המסמכים הרשמיים של הנכס - היתרי בנייה, תשריטים, טופס 4, ועוד.
                    </p>
                    <div className="building-file-doc-types" aria-label="סוגי מסמכים בתיק בניין">
                      <span className="building-file-doc-type">היתרי בנייה</span>
                      <span className="building-file-doc-type">תשריטים</span>
                      <span className="building-file-doc-type">טופס 4</span>
                    </div>
                  </div>
                  <a 
                    className="btn btn-sm btn-secondary"
                    style={{ flexShrink: 0 }}
                  href={(() => {
                    const g = propertyInfo.gush;
                    const h = propertyInfo.helka;
                    const m = municipality || '';
                    const raananaBuildingFileFallback = (() => {
                      const plansArray = Array.isArray(results?.plans)
                        ? results.plans
                        : Array.isArray(results)
                          ? results
                          : [];
                      const match = plansArray.find(plan => {
                        const muni = plan.municipality || plan.municipalSource || plan.county || '';
                        const muniLower = typeof muni === 'string' ? muni.toLowerCase() : '';
                        const isRaanana = muni === 'רעננה' || muniLower.includes("ra'anana") || muniLower.includes('raanana');
                        return isRaanana && plan.buildingFileNumber;
                      });
                      return match?.buildingFileNumber || null;
                    })();
                    const buildingFileNumber = propertyInfo?.buildingData?.buildingFileNumber 
                      || propertyInfo?.buildingFileNumber 
                      || propertyInfo?.buildingData?.buildingFile
                      || raananaBuildingFileFallback;
                    
                    // Municipal building file URLs
                    const tikBinyanUrls = {
                      'תל אביב-יפו': `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${g}_${h}`,
                      'תל אביב יפו': `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${g}_${h}`,
                      'רמת גן': `https://handasa.ramat-gan.muni.il/gush2/#unified/${g}/${h}`,
                      'ירושלים': `https://www.jerusalem.muni.il/he/residents/construction-and-licensing/buildinglicensing/tik-binyan/?gush=${g}&helka=${h}`,
                      'גבעתיים': `https://www.givatayim.muni.il/159/#search/GetTikimByGush&siteid=${g ? 98 : ''}&g=${g || ''}&h=${h || ''}&m=&l=true&arguments=siteid,g,h,m,l`,
                      // Complot municipalities - use their portals
                      'חיפה': `https://haifa.complot.co.il/newengine/Pages/buildings2.aspx#search/GetTikimByGush&siteid=16&g=${g || ''}&h=${h || ''}&m=&l=true&arguments=siteid,g,h,m,l`,
                      'רחובות': `https://rechovot.complot.co.il/gush2/#gush/${g}/${h}`,
                      'אשדוד': `https://ashdod.complot.co.il/gush2/#gush/${g}/${h}`,
                      'אשקלון': `https://ashkelon.complot.co.il/gush2/#gush/${g}/${h}`,
                      'רעננה': buildingFileNumber 
                        ? `https://www.raanana.muni.il/residents/engineering/building-permit/buildingportfolioarchive/#building/${buildingFileNumber}`
                        : `https://www.raanana.muni.il/residents/engineering/building-permit/buildingportfolioarchive/#search/GetTikimByGush&siteid=15&g=${g || ''}&h=${h || ''}&m=&l=true&arguments=siteid,g,h,m,l`,
                      'באר שבע': `https://gis.br7.org.il/br7gis/`,
                      'בת ים': `https://batyam.complot.co.il/gush2/#gush/${g}/${h}`,
                      'הוד השרון': `https://hodhasharon.complot.co.il/gush2/#gush/${g}/${h}`,
                      'הרצליה': `https://gis.herzliya.muni.il/`,
                      'חולון': `https://www.holon.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'טבריה': `https://www.tiberias.muni.il/`,
                      'יבנה': `https://yavne.complot.co.il/gush2/#gush/${g}/${h}`,
                      'כפר סבא': `https://www.kfar-saba.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'מודיעין': `https://modiin.complot.co.il/gush2/#gush/${g}/${h}`,
                      'מודיעין מכבים רעות': `https://modiin.complot.co.il/gush2/#gush/${g}/${h}`,
                      'נהריה': `https://nahariya.complot.co.il/gush2/#gush/${g}/${h}`,
                      'נצרת': `https://nazareth.complot.co.il/gush2/#gush/${g}/${h}`,
                      'פתח תקווה': `https://www.petah-tikva.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'פתח תקוה': `https://www.petah-tikva.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'ראשון לציון': `https://rishonlezion.complot.co.il/gush2/#gush/${g}/${h}`,
                      'רמת השרון': `https://www.ramat-hasharon.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'אילת': `https://eilat.complot.co.il/gush2/#gush/${g}/${h}`,
                      'דימונה': `https://dimona.complot.co.il/gush2/#gush/${g}/${h}`,
                      'שדרות': `https://sderot.complot.co.il/gush2/#gush/${g}/${h}`,
                      'בני ברק': `https://www.bnei-brak.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'אור יהודה': `https://www.or-yehuda.muni.il/`,
                      'קרית גת': `https://qiryat-gat.complot.co.il/gush2/#gush/${g}/${h}`,
                      'בית שמש': `https://www.bet-shemesh.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'יקנעם': `https://www.yokneam.muni.il/`,
                      'יקנעם עילית': `https://www.yokneam.muni.il/`,
                      'מעלות תרשיחא': `https://www.maalot.muni.il/`,
                      'צפת': `https://www.zefat.muni.il/`,
                      'אופקים': `https://www.ofakim.muni.il/`,
                      'טירה': `https://www.tire.muni.il/`,
                      'רעננה (תיקי בניין)': `https://www.raanana.muni.il/residents/engineering/building-permit/buildingportfolioarchive/`,
                    };
                    
                    return tikBinyanUrls[m] || `https://www.govmap.gov.il/?c=${propertyInfo.coordinates?.x || ''},${propertyInfo.coordinates?.y || ''}&z=9`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                    <i className="fi fi-rr-building"></i> פתח תיק בעיריית {municipality}
                </a>
                </div>
              </div>
              )}
            </div>{/* Close market-data-content */}
            </div>{/* Close market-data-card */}
            </>
            )}

            </div>
            <div className="property-map-column">
              <LocationMap 
                coordinates={propertyInfo?.coordinates || results?.coordinates} 
                address={address} 
                stacked={true}
              />
              
                  </div>
          </div>

      {/* Permits Card - Standalone from plans */}
      {(isLoading && !results) || hasPermits ? (
      <div className="market-data-card real-data">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-bank"></i> בקשות והיתרים</h3>
        </div>
        <div className="market-data-content plans-content">

      {/* Tel Aviv Municipal Permits Section - First for visibility */}
      {isLoading && !results ? (
        <TelAvivShimmer count={2} />
      ) : telAvivPlans && telAvivPlans.length > 0 && (
        renderPlanPreviewSection(
          telAvivPlans,
          'tel-aviv',
          'בקשות והיתרים עיריית תל אביב',
          'fi-rr-bank',
          telAvivExpanded,
          setTelAvivExpanded
        )
      )}

      {/* Rehovot Municipal Permits */}
      {rehovotPlans && rehovotPlans.length > 0 && (
        renderPlanPreviewSection(
          rehovotPlans,
          'rehovot',
          'בקשות והיתרים עיריית רחובות',
          'fi-rr-bank',
          rehovotExpanded,
          setRehovotExpanded
        )
      )}

      {/* Jerusalem Municipal Permits */}
      {jerusalemPlans && jerusalemPlans.length > 0 && (
        renderPlanPreviewSection(
          jerusalemPlans,
          'jerusalem',
          'בקשות והיתרים עיריית ירושלים',
          'fi-rr-bank',
          jerusalemExpanded,
          setJerusalemExpanded
        )
      )}

      {/* Dynamic Complot Municipal PERMITS - Right after Tel Aviv, expanded by default (like Tel Aviv) */}
      {!isLoading && complotPermitsMap && Object.entries(complotPermitsMap).map(([municipalityName, permits]) => (
        permits && permits.length > 0 && (
          <div key={`permits-${municipalityName}`}>
            {renderPlanPreviewSection(
              permits,
              `complot-permits-${municipalityName.replace(/\s+/g, '-')}`,
              `בקשות והיתרים עיריית ${municipalityName}`,
              'fi-rr-bank',
              complotExpanded[`permits-${municipalityName}`] !== false, // Default to true (expanded like Tel Aviv)
              (expanded) => setComplotExpanded(prev => ({ ...prev, [`permits-${municipalityName}`]: expanded }))
            )}
          </div>
        )
      ))}

        </div>{/* Close market-data-content */}
      </div>
      ) : null}

      {/* Plans Card - Contains all plan sections */}
      <div id="section-plans" className="market-data-card real-data">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-document"></i> תוכניות</h3>
        </div>
        <div className="market-data-content plans-content">

        {/* Other Municipal Plans Sections */}
        {console.log('🔍 Before Municipal Sections:', { isLoading, hasResults: !!results, telAvivLength: telAvivPlans?.length, rehovotLength: rehovotPlans?.length, jerusalemLength: jerusalemPlans?.length, complotPlans: Object.keys(complotPlansMap || {}), complotPermits: Object.keys(complotPermitsMap || {}), otherLength: governmentPlans?.length })}
        {isLoading && !results ? (
          <>
            <MunicipalSectionShimmer count={2} />
            <MunicipalSectionShimmer count={2} />
            <MunicipalSectionShimmer count={3} />
          </>
        ) : (
          <>
            {console.log('🔍 Rendering Municipal Sections - Tel Aviv:', telAvivPlans?.length, 'Tel Aviv Klal:', telAvivKlalPlans?.length, 'Tel Aviv Local:', telAvivLocalPlans?.length, 'Rehovot:', rehovotPlans?.length, 'Jerusalem:', jerusalemPlans?.length, 'Complot Plans:', Object.keys(complotPlansMap || {}), 'Complot Permits:', Object.keys(complotPermitsMap || {}), 'Government:', governmentPlans?.length)}
            {/* Tel Aviv section moved above - keeping this comment for reference */}

            {/* Dynamic Complot Municipal PLANS Sections */}
            {complotPlansMap && Object.entries(complotPlansMap).map(([municipalityName, plans]) => (
              plans && plans.length > 0 && (
                <div key={`plans-${municipalityName}`}>
                  {renderPlanPreviewSection(
                    plans,
                    `complot-plans-${municipalityName.replace(/\s+/g, '-')}`,
                    `תוכניות עיריית ${municipalityName}`,
                    'fi-rr-city',
                    complotExpanded[`plans-${municipalityName}`] || false,
                    (expanded) => setComplotExpanded(prev => ({ ...prev, [`plans-${municipalityName}`]: expanded }))
                  )}
                </div>
              )
            ))}

            {/* Tel Aviv Local Plans Section */}
            {telAvivLocalPlans && telAvivLocalPlans.length > 0 && (
              renderPlanPreviewSection(
                telAvivLocalPlans,
                'tel-aviv-local',
                'תוכניות עיריית תל אביב - מקומיות',
                'fi-rr-map',
                telAvivLocalExpanded,
                setTelAvivLocalExpanded
              )
            )}

            {/* Tel Aviv Klal Plans Section */}
            {telAvivKlalPlans && telAvivKlalPlans.length > 0 && (
              renderPlanPreviewSection(
                telAvivKlalPlans,
                'tel-aviv-klal',
                'תוכניות עיריית תל אביב - כלל עירוניות',
                'fi-rr-ruler-combined',
                telAvivKlalExpanded,
                setTelAvivKlalExpanded
              )
            )}

            {/* Government Plans Section (Collapsible) */}
            {governmentPlans && governmentPlans.length > 0 && (
              renderPlanPreviewSection(
                governmentPlans,
                'government',
                'תוכניות ממשלתיות',
                'fi-rr-bank',
                otherResultsExpanded,
                setOtherResultsExpanded
              )
            )}
          </>
        )}

        {results && results.length === 0 && (
          <div className="no-plans-message">
            <i className="fi fi-rr-search"></i>
            <p>לא נמצאו תוכניות עבור הכתובת: {address}</p>
            <p className="secondary">נסה לחפש בכתובת אחרת או פנה אלינו לבדיקה ידנית</p>
          </div>
        )}

        </div>{/* Close market-data-content */}
      </div>{/* Close Plans Card */}

      {/* Market Data Section */}
      <div id="section-market">
      {(feasibilityData?.feasibilityInput?.marketData || feasibilityData?.feasibilityInput?.expectedSalePricePerSqmNIS) ? (
        <MarketData 
          marketData={feasibilityData.feasibilityInput.marketData} 
          pricePerSqm={feasibilityData.feasibilityInput.expectedSalePricePerSqmNIS} 
        />
      ) : (
        isLoading ? <MarketDataShimmer /> : null
      )}
      </div>

        </div>

        {/* Feasibility Analysis Section - Inline on same page */}
        <div id="feasibility-section" className="feasibility-section-wrapper">
        
        {/* Feasibility Report Section - Pre-calculated data from App.js */}
        {(feasibilityData || isLoading) && (
          <div className="feasibility-action-section">
              <FeasibilityScore 
                searchResults={feasibilitySearchResults}
              onFeasibilityCalculated={(data) => {
                console.log('✅ Feasibility received:', data);
                }}
              shouldCalculate={false}
              precalculatedData={feasibilityData}
              isParentLoading={isLoading}
              aiRecommendation={aiRecommendation}
              setAiRecommendation={setAiRecommendation}
              aiAnalysisUnlocked={aiAnalysisUnlocked}
              setAiAnalysisUnlocked={setAiAnalysisUnlocked}
              aiLoading={aiLoading}
              setAiLoading={setAiLoading}
              aiError={aiError}
              setAiError={setAiError}
              aiDisabledReason={aiDisabledReason}
              onTabChange={onTabChange}
              onApplyFeasibilityOverride={onApplyFeasibilityOverride}
              feasibilityOverrideBusy={feasibilityOverrideBusy}
              />
          </div>
        )}
        </div>
        </>
        )}
        {/* End Combined Property & Feasibility Section */}
        {selectedPlanForDrawer ? (
          <PlanDetailsDrawer
            key={[
              selectedPlanForDrawer?.id || '',
              selectedPlanForDrawer?.planNumber || selectedPlanForDrawer?.plan_number || '',
              selectedPlanForDrawer?._taSourceLayer || '',
              selectedPlanForDrawer?._taSourceObjectId || '',
              selectedPlanForDrawer?.requestNumber || selectedPlanForDrawer?.permitNumber || '',
            ].join('|')}
            plan={selectedPlanForDrawer}
            onClose={() => setSelectedPlanForDrawer(null)}
          />
        ) : null}
      </div>
    );
  }

  // Non-embedded mode: return full page with app wrapper and back button
  return (
    <div className="app">
      <div className="results-container">
        <SectionNav />
        <div className="results-header" ref={resultsTopRef}>
          {results && results.length > 0 && (
            <div className="results-summary">
              <p className="summary-text">
                נמצאו <strong>{results.length}</strong> תוכניות אדריכליות
              </p>
            </div>
          )}
        </div>

        {/* API Error Messages - Global */}
        {Object.keys(apiErrors).length > 0 && !sourceHealth?.criticalSourceUnavailable && (
          <div className="api-errors-container">
            {apiErrors.critical && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-exclamation"></i> שגיאת מערכת</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.critical}</p>
                </div>
                <div className="result-actions">
                  <button className="btn btn-primary" onClick={() => window.location.reload()}>
                    <i className="fi fi-rr-refresh"></i> נסה שוב
                  </button>
                </div>
              </div>
            )}
            
            {apiErrors.propertyInfo && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-home"></i> מידע קרקע</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.propertyInfo}</p>
                </div>
                <div className="result-actions">
                  <button className="btn btn-primary" onClick={onRetryPropertyInfo}>
                    🔄 נסה להתחבר שוב
                  </button>
                </div>
              </div>
            )}
            
            {apiErrors.plans && (
              <div className="result-card" style={{ borderLeftColor: 'rgba(var(--rgb-punch-red), 0.6)' }}>
                <div className="result-header">
                  <h3 className="result-title"><i className="fi fi-rr-clipboard"></i> תוכניות בינוי</h3>
                </div>
                <div className="result-details">
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', gridColumn: '1 / -1' }}>{apiErrors.plans}</p>
                </div>
                <div className="result-actions">
                  {onRetryPlans && (
                    <button className="btn btn-primary" onClick={onRetryPlans}>
                      🔄 נסה להתחבר שוב
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Property & Plans Information Section */}
        {(activeTab === 'property' || activeTab === 'feasibility') && (
        <>
        {limitedDataWarningJSX}
        {updatesNotificationFormJSX}
        <div className="property-section-wrapper">
          
          <div className="property-tab-layout">
            <div className="property-info-column">
              {/* Unified Data Summary Card - Merged ריכוז נתונים & נתוני נכס */}
              {propertyInfo && (
              <>
              <div id="section-summary" className="market-data-card real-data property-summary-card">
                {/* Section Header - Same style as נתוני שוק ומחירים */}
                <div className="market-data-header">
                  <h3 className="market-data-title"><i className="fi fi-rr-file-invoice"></i> ריכוז נתונים</h3>
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{formatDate(searchTimestamp)} {formatTime(searchTimestamp)}</span>
                </div>
                
                <div className="market-data-content">
                {/* Summary Text */}
                <div className="data-item summary-text-item">
                  <p className="summary-main-text">
                    המערכת בדקה <strong>{getSourcesCount()} מקורות רשמיים</strong> וריכזה עבורך את המידע העדכני ביותר.
                  </p>
                </div>
                
                {/* Divider after summary text */}
                <div style={{ margin: '2px 0', borderBottom: '1px solid var(--border-light)', gridColumn: '1 / -1' }}></div>

              {/* Property Identification */}
              <div className="data-item">
                <span className="data-label">גוש:</span>
                <span className="data-value">{propertyInfo?.gush || 'לא זוהה'}</span>
                <p className="data-description">מספר הגוש במרשם המקרקעין.</p>
              </div>
              
              <div className="data-item">
                <span className="data-label">חלקה:</span>
                <span className="data-value">{propertyInfo?.helka || 'לא זוהתה'}</span>
                <p className="data-description">מספר החלקה במרשם המקרקעין.</p>
              </div>
              
              {propertyInfo?.subHelka && (
                <div className="data-item">
                  <span className="data-label">תת-חלקה:</span>
                  <span className="data-value">{propertyInfo.subHelka}</span>
                  <p className="data-description">מספר תת-חלקה במרשם המקרקעין.</p>
                </div>
              )}
              
              <div className="data-item">
                <span className="data-label">רשות מקומית:</span>
                <span className="data-value">{propertyInfo?.municipality || 'לא זוהתה רשות מקומית'}</span>
                <p className="data-description">הרשות המקומית בה ממוקם הנכס.</p>
              </div>
              
              {displayPropertyAddress && (
                <div className="data-item">
                  <span className="data-label">כתובת:</span>
                  <span className="data-value">{displayPropertyAddress}</span>
                  <p className="data-description">הכתובת המלאה של הנכס.</p>
                </div>
              )}
              
              {getLandUseDisplay() && (
                <div className="data-item">
                  <span className="data-label">ייעוד קרקע:</span>
                  <span className="data-value">{getLandUseDisplay()}</span>
                  <p className="data-description">ייעוד הקרקע לפי תוכניות מאושרות.</p>
                </div>
              )}

              {/* Municipal Building Data (if available) */}
              {propertyInfo.buildingData && (
                <>
              {propertyInfo.buildingData.buildingNumber && (
                <div className="data-item">
                  <span className="data-label">מספר מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.buildingNumber}</span>
                  <p className="data-description">מספר הזיהוי הפנימי של הבניין במערכות העירוניות.</p>
                </div>
              )}
              {(propertyInfo.buildingData.floors || effectiveBuildingData?.floors) && (
                <div className="data-item">
                  <span className="data-label">מספר קומות:</span>
                  <span className="data-value">{propertyInfo.buildingData.floors || effectiveBuildingData.floors}</span>
                  {effectiveBuildingData?.floorsOverrideReason ? (
                    <p className="data-description" style={{ color: 'var(--status-warning-text)' }}>⚠️ {effectiveBuildingData.floorsOverrideReason}</p>
                  ) : (
                    <p className="data-description">כמות הקומות הרשומה לבניין.</p>
                  )}
                </div>
              )}
              {propertyInfo.buildingData.height && (
                <div className="data-item">
                  <span className="data-label">גובה מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.height} מ׳</span>
                  <p className="data-description">הגובה הרשום, אינדיקטיבי.</p>
                </div>
              )}
              {propertyInfo.buildingData.area && (
                <div className="data-item">
                  <span className="data-label">שטח חלקה:</span>
                  <span className="data-value">{propertyInfo.buildingData.area} מ״ר</span>
                  <p className="data-description">השטח המדוד/רשום של החלקה.</p>
                </div>
              )}
              {effectiveBuildingData?.year && !effectiveBuildingData?.yearEstimated && (
                <div className="data-item">
                  <span className="data-label">שנת בנייה:</span>
                  <span className="data-value">{String(effectiveBuildingData.year) === '1900' ? 'לא ידוע' : effectiveBuildingData.year}</span>
                  <p className="data-description">
                    {effectiveBuildingData.yearNote || 'השנה בה נרשמה בניית המבנה/טופס אכלוס.'}
                  </p>
                </div>
              )}
              {propertyInfo.buildingData.buildingType && (
                <div className="data-item">
                  <span className="data-label">סוג מבנה:</span>
                  <span className="data-value">{propertyInfo.buildingData.buildingType}</span>
                  <p className="data-description">סיווג טיפוסי (למשל מגורים/מעורב/ציבורי).</p>
                </div>
              )}
              {propertyInfo.buildingData.hasColumns && (
                <div className="data-item">
                  <span className="data-label">עמודים:</span>
                  <span className="data-value">{propertyInfo.buildingData.hasColumns}</span>
                  <p className="data-description">האם קומת העמודים פתוחה/קיימת לפי הרישום.</p>
                </div>
              )}
              {propertyInfo.buildingData.asbestLevel && propertyInfo.buildingData.asbestLevel !== 'לא ידוע' && (
                <div className="data-item">
                  <span className="data-label">סכנת אסבסט:</span>
                  <span className="data-value">{propertyInfo.buildingData.asbestLevel}</span>
                  <p className="data-description">אינדיקציה עירונית לנוכחות אסבסט/חשש.</p>
                </div>
              )}
                </>
              )}

              {/* GovMap Extended Data - National Data */}
              <GovMapData 
                coordinates={propertyInfo?.coordinates || results?.coordinates}
                propertyInfo={propertyInfo}
              />
              
              {/* Divider before tik binyan */}
              {municipality && (
                <div style={{ margin: '2px 0', borderBottom: '1px solid var(--border-light)', gridColumn: '1 / -1' }}></div>
              )}
              
              {/* Building File (Tik Binyan) */}
              {municipality && (
              <div style={{ 
                gridColumn: '1 / -1'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="fi fi-rr-folder-open" style={{ color: 'var(--color-cerulean)', fontSize: '18px' }}></i>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>תיק בניין</span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 10px 0', lineHeight: '1.5' }}>
                      תיק הבניין מכיל את כל המסמכים הרשמיים של הנכס - היתרי בנייה, תשריטים, טופס 4, ועוד.
                    </p>
                    <div className="building-file-doc-types" aria-label="סוגי מסמכים בתיק בניין">
                      <span className="building-file-doc-type">היתרי בנייה</span>
                      <span className="building-file-doc-type">תשריטים</span>
                      <span className="building-file-doc-type">טופס 4</span>
                    </div>
                  </div>
                  <a 
                    className="btn btn-sm btn-secondary"
                    style={{ flexShrink: 0 }}
                  href={(() => {
                    const g = propertyInfo.gush;
                    const h = propertyInfo.helka;
                    const m = municipality || '';
                    
                    // Municipal building file URLs
                    const tikBinyanUrls = {
                      'תל אביב-יפו': `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${g}_${h}`,
                      'תל אביב יפו': `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${g}_${h}`,
                      'רמת גן': `https://handasa.ramat-gan.muni.il/gush2/#unified/${g}/${h}`,
                      'ירושלים': `https://www.jerusalem.muni.il/he/residents/construction-and-licensing/buildinglicensing/tik-binyan/?gush=${g}&helka=${h}`,
                      'גבעתיים': `https://www.givatayim.muni.il/159/#search/GetTikimByGush&siteid=${g ? 98 : ''}&g=${g || ''}&h=${h || ''}&m=&l=true&arguments=siteid,g,h,m,l`,
                      // Complot municipalities - use their portals
                      'חיפה': `https://haifa.complot.co.il/newengine/Pages/buildings2.aspx#search/GetTikimByGush&siteid=16&g=${g || ''}&h=${h || ''}&m=&l=true&arguments=siteid,g,h,m,l`,
                      'רחובות': `https://rechovot.complot.co.il/gush2/#gush/${g}/${h}`,
                      'אשדוד': `https://ashdod.complot.co.il/gush2/#gush/${g}/${h}`,
                      'אשקלון': `https://ashkelon.complot.co.il/gush2/#gush/${g}/${h}`,
                      'באר שבע': `https://gis.br7.org.il/br7gis/`,
                      'בת ים': `https://batyam.complot.co.il/gush2/#gush/${g}/${h}`,
                      'הוד השרון': `https://hodhasharon.complot.co.il/gush2/#gush/${g}/${h}`,
                      'הרצליה': `https://gis.herzliya.muni.il/`,
                      'חולון': `https://www.holon.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'טבריה': `https://www.tiberias.muni.il/`,
                      'יבנה': `https://yavne.complot.co.il/gush2/#gush/${g}/${h}`,
                      'כפר סבא': `https://www.kfar-saba.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'מודיעין': `https://modiin.complot.co.il/gush2/#gush/${g}/${h}`,
                      'מודיעין מכבים רעות': `https://modiin.complot.co.il/gush2/#gush/${g}/${h}`,
                      'נהריה': `https://nahariya.complot.co.il/gush2/#gush/${g}/${h}`,
                      'נצרת': `https://nazareth.complot.co.il/gush2/#gush/${g}/${h}`,
                      'פתח תקווה': `https://www.petah-tikva.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'פתח תקוה': `https://www.petah-tikva.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'ראשון לציון': `https://rishonlezion.complot.co.il/gush2/#gush/${g}/${h}`,
                      'רמת השרון': `https://www.ramat-hasharon.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'אילת': `https://eilat.complot.co.il/gush2/#gush/${g}/${h}`,
                      'דימונה': `https://dimona.complot.co.il/gush2/#gush/${g}/${h}`,
                      'שדרות': `https://sderot.complot.co.il/gush2/#gush/${g}/${h}`,
                      'בני ברק': `https://www.bnei-brak.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'אור יהודה': `https://www.or-yehuda.muni.il/`,
                      'קרית גת': `https://qiryat-gat.complot.co.il/gush2/#gush/${g}/${h}`,
                      'בית שמש': `https://www.bet-shemesh.muni.il/Residents/Engineering/Pages/default.aspx`,
                      'יקנעם': `https://www.yokneam.muni.il/`,
                      'יקנעם עילית': `https://www.yokneam.muni.il/`,
                      'מעלות תרשיחא': `https://www.maalot.muni.il/`,
                      'צפת': `https://www.zefat.muni.il/`,
                      'אופקים': `https://www.ofakim.muni.il/`,
                      'טירה': `https://www.tire.muni.il/`,
                    };
                    
                    return tikBinyanUrls[m] || `https://www.govmap.gov.il/?c=${propertyInfo.coordinates?.x || ''},${propertyInfo.coordinates?.y || ''}&z=9`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                    <i className="fi fi-rr-building"></i> פתח תיק בעיריית {municipality}
                </a>
                </div>
              </div>
              )}
            </div>{/* Close market-data-content */}
            </div>{/* Close market-data-card */}
            </>
            )}

            </div>
            <div className="property-map-column">
              <LocationMap 
                coordinates={propertyInfo?.coordinates || results?.coordinates} 
                address={address} 
                stacked={true}
              />
              
                  </div>
          </div>

      {/* Permits Card - Standalone from plans */}
      {(isLoading && !results) || hasPermits ? (
      <div className="market-data-card real-data">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-bank"></i> בקשות והיתרים</h3>
        </div>
        <div className="market-data-content plans-content">

      {/* Tel Aviv Municipal Permits Section - First for visibility */}
      {isLoading && !results ? (
        <TelAvivShimmer count={2} />
      ) : telAvivPlans && telAvivPlans.length > 0 && (
        renderPlanPreviewSection(
          telAvivPlans,
          'tel-aviv',
          'בקשות והיתרים עיריית תל אביב',
          'fi-rr-bank',
          telAvivExpanded,
          setTelAvivExpanded
        )
      )}

      {/* Rehovot Municipal Permits */}
      {rehovotPlans && rehovotPlans.length > 0 && (
        renderPlanPreviewSection(
          rehovotPlans,
          'rehovot',
          'בקשות והיתרים עיריית רחובות',
          'fi-rr-bank',
          rehovotExpanded,
          setRehovotExpanded
        )
      )}

      {/* Jerusalem Municipal Permits */}
      {jerusalemPlans && jerusalemPlans.length > 0 && (
        renderPlanPreviewSection(
          jerusalemPlans,
          'jerusalem',
          'בקשות והיתרים עיריית ירושלים',
          'fi-rr-bank',
          jerusalemExpanded,
          setJerusalemExpanded
        )
      )}

      {/* Dynamic Complot Municipal PERMITS - Right after Tel Aviv, expanded by default (like Tel Aviv) */}
      {!isLoading && complotPermitsMap && Object.entries(complotPermitsMap).map(([municipalityName, permits]) => (
        permits && permits.length > 0 && (
          <div key={`permits-${municipalityName}`}>
            {renderPlanPreviewSection(
              permits,
              `complot-permits-${municipalityName.replace(/\s+/g, '-')}`,
              `בקשות והיתרים עיריית ${municipalityName}`,
              'fi-rr-bank',
              complotExpanded[`permits-${municipalityName}`] !== false, // Default to true (expanded like Tel Aviv)
              (expanded) => setComplotExpanded(prev => ({ ...prev, [`permits-${municipalityName}`]: expanded }))
            )}
          </div>
        )
      ))}

        </div>{/* Close market-data-content */}
      </div>
      ) : null}

      {/* Plans Card - Contains all plan sections */}
      <div id="section-plans" className="market-data-card real-data">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-document"></i> תוכניות</h3>
        </div>
        <div className="market-data-content plans-content">

        {/* Other Municipal Plans Sections */}
        {console.log('🔍 Before Municipal Sections:', { isLoading, hasResults: !!results, telAvivLength: telAvivPlans?.length, rehovotLength: rehovotPlans?.length, jerusalemLength: jerusalemPlans?.length, complotPlans: Object.keys(complotPlansMap || {}), complotPermits: Object.keys(complotPermitsMap || {}), otherLength: governmentPlans?.length })}
        {isLoading && !results ? (
          <>
            <MunicipalSectionShimmer count={2} />
            <MunicipalSectionShimmer count={2} />
            <MunicipalSectionShimmer count={3} />
          </>
        ) : (
          <>
            {console.log('🔍 Rendering Municipal Sections - Tel Aviv:', telAvivPlans?.length, 'Tel Aviv Klal:', telAvivKlalPlans?.length, 'Tel Aviv Local:', telAvivLocalPlans?.length, 'Rehovot:', rehovotPlans?.length, 'Jerusalem:', jerusalemPlans?.length, 'Complot Plans:', Object.keys(complotPlansMap || {}), 'Complot Permits:', Object.keys(complotPermitsMap || {}), 'Government:', governmentPlans?.length)}
            {/* Tel Aviv section moved above - keeping this comment for reference */}

        {/* Dynamic Complot Municipal PLANS Sections */}
        {complotPlansMap && Object.entries(complotPlansMap).map(([municipalityName, plans]) => (
          plans && plans.length > 0 && (
            <div key={`plans-${municipalityName}`}>
              {renderPlanPreviewSection(
                plans,
                `complot-plans-${municipalityName.replace(/\s+/g, '-')}`,
                `תוכניות עיריית ${municipalityName}`,
                'fi-rr-city',
                complotExpanded[`plans-${municipalityName}`] || false,
                (expanded) => setComplotExpanded(prev => ({ ...prev, [`plans-${municipalityName}`]: expanded }))
              )}
            </div>
          )
        ))}

        {/* Tel Aviv Local Plans Section */}
        {telAvivLocalPlans && telAvivLocalPlans.length > 0 && (
          renderPlanPreviewSection(
            telAvivLocalPlans,
            'tel-aviv-local',
            'תוכניות עיריית תל אביב - מקומיות',
            'fi-rr-map',
            telAvivLocalExpanded,
            setTelAvivLocalExpanded
          )
        )}

        {/* Tel Aviv Klal Plans Section */}
        {telAvivKlalPlans && telAvivKlalPlans.length > 0 && (
          renderPlanPreviewSection(
            telAvivKlalPlans,
            'tel-aviv-klal',
            'תוכניות עיריית תל אביב - כלל עירוניות',
            'fi-rr-ruler-combined',
            telAvivKlalExpanded,
            setTelAvivKlalExpanded
          )
        )}

        {/* Government Plans Section (Collapsible) */}
        {governmentPlans && governmentPlans.length > 0 && (
          renderPlanPreviewSection(
            governmentPlans,
            'government',
            'תוכניות ממשלתיות',
            'fi-rr-bank',
            otherResultsExpanded,
            setOtherResultsExpanded
          )
        )}
          </>
        )}

        {results && results.length === 0 && (
          <div className="no-plans-message">
            <i className="fi fi-rr-search"></i>
            <p>לא נמצאו תוכניות עבור הכתובת: {address}</p>
            <p className="secondary">נסה לחפש בכתובת אחרת או פנה אלינו לבדיקה ידנית</p>
          </div>
        )}

        </div>{/* Close market-data-content */}
      </div>{/* Close Plans Card */}

      {/* Market Data Section */}
      <div id="section-market">
      {(feasibilityData?.feasibilityInput?.marketData || feasibilityData?.feasibilityInput?.expectedSalePricePerSqmNIS) ? (
        <MarketData 
          marketData={feasibilityData.feasibilityInput.marketData} 
          pricePerSqm={feasibilityData.feasibilityInput.expectedSalePricePerSqmNIS} 
        />
      ) : (
        isLoading ? <MarketDataShimmer /> : null
      )}
      </div>

        </div>

        {/* Feasibility Analysis Section - Inline on same page */}
        <div id="feasibility-section" className="feasibility-section-wrapper">
        
        {/* Feasibility Report Section - Pre-calculated data from App.js */}
        {(feasibilityData || isLoading) && (
          <div className="feasibility-action-section">
              <FeasibilityScore 
                searchResults={feasibilitySearchResults}
              onFeasibilityCalculated={(data) => {
                console.log('✅ Feasibility received:', data);
                }}
              shouldCalculate={false}
              precalculatedData={feasibilityData}
              isParentLoading={isLoading}
              aiRecommendation={aiRecommendation}
              setAiRecommendation={setAiRecommendation}
              aiAnalysisUnlocked={aiAnalysisUnlocked}
              setAiAnalysisUnlocked={setAiAnalysisUnlocked}
              aiLoading={aiLoading}
              setAiLoading={setAiLoading}
              aiError={aiError}
              setAiError={setAiError}
              aiDisabledReason={aiDisabledReason}
              onTabChange={onTabChange}
              onApplyFeasibilityOverride={onApplyFeasibilityOverride}
              feasibilityOverrideBusy={feasibilityOverrideBusy}
              />
          </div>
        )}
        </div>
        </>
        )}
        {/* End Combined Property & Feasibility Section */}
        {selectedPlanForDrawer ? (
          <PlanDetailsDrawer
            key={[
              selectedPlanForDrawer?.id || '',
              selectedPlanForDrawer?.planNumber || selectedPlanForDrawer?.plan_number || '',
              selectedPlanForDrawer?._taSourceLayer || '',
              selectedPlanForDrawer?._taSourceObjectId || '',
              selectedPlanForDrawer?.requestNumber || selectedPlanForDrawer?.permitNumber || '',
            ].join('|')}
            plan={selectedPlanForDrawer}
            onClose={() => setSelectedPlanForDrawer(null)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ResultsPage;
